/**
 * Notches v1 — main page (matches WFS dashboard design language).
 *
 * Symbol tabs (filtered by user's per-symbol subscriptions),
 * preset toggle (Conservative / Moderate / Aggressive), skew + macro state
 * tiles, and per-DTE sections. Each DTE section is collapsible and contains
 * compact rows for the WFS pick + Claude pick + ChatGPT pick at that DTE.
 * Each row has a "Why" toggle that expands the reasoning inline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessSymbol } from '../../lib/entitlements';
import { MarketPulseModal, SkewCard, ChatSidebar } from '../spx/SpxSignalsV2';
import '../Dashboard.css';
import {
  notchesService,
  isEmpty,
  NotchesSymbol,
  NotchesPreset,
  NotchesResponse,
  NotchesCandidate,
  NotchesMacroFactor,
  RegimeDebug,
} from '../../services/notchesService';
import { marketRegimeService } from '../../services/marketRegimeService';
import type { MacroBlock } from '../../services/marketRegimeService';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';

// ── WFS color palette (matches SpxSignalsV2.tsx pattern) ─────────────────────
const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  blue: '#3b82f6',
  blueBg: '#3b82f620',
  amber: '#d97706',
  amberBg: '#d9770620',
  red: '#dc2626',
  redBg: '#dc262620',
  purple: '#8b5cf6',
  purpleBg: '#8b5cf620',
  orange: '#f97316',
  orangeBg: '#f9731620',
};

const fmt = (n: any, d = 2) => (n == null ? '—' : Number(n).toFixed(d));
const fmtSigned = (n: any, d = 2) => {
  if (n == null) return '—';
  const v = Number(n);
  return (v >= 0 ? '+' : '') + v.toFixed(d);
};
const fmtDate = (s: string) => {
  try {
    return new Date(s).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return s || '—';
  }
};
const fmtExp = (s: string) => {
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return s || '—';
  }
};
// Compact "Mon DD" format for narrow table cells (year omitted — DTE column already implies it).
const fmtExpShort = (s: string) => {
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return s || '—';
  }
};

// Calendar days from today (ET) to the expiration date.
// Producer's stored `dte` is snapshot-time relative — stale by 2-3 days after
// weekend or holiday — so display uses this live calculation instead.
const liveDte = (expiration: string | undefined | null): number | null => {
  if (!expiration) return null;
  try {
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
    const today = new Date(todayStr + 'T00:00:00Z').getTime();
    const exp = new Date(expiration + 'T00:00:00Z').getTime();
    return Math.round((exp - today) / 86400000);
  } catch {
    return null;
  }
};

const badge = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '0.15rem 0.5rem',
  borderRadius: 12,
  fontSize: '0.7rem',
  fontWeight: 600,
  background: bg,
  color,
});

type PickSource = 'WFS' | 'Claude' | 'ChatGPT';

const SOURCE_STYLE: Record<PickSource, { color: string; bg: string }> = {
  WFS:     { color: c.amber, bg: c.amberBg },
  Claude:  { color: c.blue,  bg: c.blueBg },
  ChatGPT: { color: '#10b981', bg: '#10b98120' },
};

// Mirror of producer's compute_exit_recommendation per-preset levels.
// Used to synthesize exit guidance for AI picks (which don't carry an
// exit_recommendation field — the same math applies regardless of who
// recommended the trade).
const PRESET_EXIT_PARAMS: Record<NotchesPreset, {
  profit_target_pct: number;
  stop_multiple: number;
  underlying_buffer_pct: number;
  trail_adjustment: number;
  hold_days_factor: number;
  max_hold_factor: number;
}> = {
  conservative: { profit_target_pct: 0.50, stop_multiple: 1.5, underlying_buffer_pct: 0.03, trail_adjustment: 0.75, hold_days_factor: 0.5, max_hold_factor: 0.6 },
  balanced:     { profit_target_pct: 0.60, stop_multiple: 2.0, underlying_buffer_pct: 0.02, trail_adjustment: 1.00, hold_days_factor: 0.4, max_hold_factor: 0.7 },
  aggressive:   { profit_target_pct: 0.70, stop_multiple: 2.5, underlying_buffer_pct: 0.01, trail_adjustment: 1.25, hold_days_factor: 0.3, max_hold_factor: 0.8 },
};

function trailMultForIv(ivPct: number): number {
  if (ivPct < 20) return 1.5;
  if (ivPct < 30) return 2.0;
  return 2.5;
}

// Mirror of producer's UNIVERSAL_RULES — used when exit_recommendation
// is synthesized client-side (AI picks). Same values either way.
const UNIVERSAL_EXIT_RULES = {
  hard_stop_delta: 0.50,
  min_corroborators_required: 1,
  min_hold_hours: 2,
  smart_let_expire: {
    short_delta_threshold: 0.10,
    market_favorable_min_pct: 0.005,
    deep_otm_threshold_pct: 0.03,
    near_expiry_dte: 7,
  },
};

function synthExitGuidance(trade: any, preset: NotchesPreset) {
  const credit = Number(trade?.credit ?? 0);
  const strike = Number(trade?.strike ?? 0);
  const dte    = Number(trade?.dte ?? 0);
  const ivPct  = Number(trade?.iv_pct ?? 0);
  if (credit <= 0 || dte <= 0) return null;
  const p = PRESET_EXIT_PARAMS[preset];
  const baseMult = trailMultForIv(ivPct);
  return {
    profit_target_credit:  Number((credit * (1 - p.profit_target_pct)).toFixed(2)),
    profit_target_pct:     p.profit_target_pct,
    hard_stop_credit:      Number((credit * p.stop_multiple).toFixed(2)),
    hard_stop_multiple:    p.stop_multiple,
    hard_stop_underlying:  strike > 0 ? Number((strike * (1 + p.underlying_buffer_pct)).toFixed(2)) : null,
    trail_mult_effective:  Number((baseMult * p.trail_adjustment).toFixed(2)),
    trail_mult_base:       baseMult,
    trail_mult_adjustment: p.trail_adjustment,
    expected_hold_days:    Math.max(1, Math.round(dte * p.hold_days_factor)),
    max_hold_days:         Math.max(1, Math.round(dte * p.max_hold_factor)),
  };
}

// ── Today's Read (subscriber-facing market summary) ─────────────────────────
//
// Translates the underlying regime + macro + skew signals into 5 plain-English
// answers the subscriber actually cares about:
//   1. Where is the market?
//   2. Where is it going?
//   3. Safe to take a new trade?
//   4. What to do today?
//   5. Aggressive or conservative?
//
// All formula details (penalties, sigma thresholds, weights, tag codes) are
// hidden. Power users can expand the "Why?" panel to see the internal market
// description, but the headline answers are derived and shown directly.

type SafetyRating = 'good' | 'okay' | 'mixed' | 'caution' | 'wait';
const SAFETY_COLOR: Record<SafetyRating, { color: string; bg: string; label: string; chipColor: string }> = {
  good:    { color: c.green,  bg: c.greenBg,  label: 'Yes — favorable setup',     chipColor: c.green  },
  okay:    { color: c.green,  bg: c.greenBg,  label: 'Yes, but be selective',     chipColor: c.green  },
  mixed:   { color: c.amber,  bg: c.amberBg,  label: 'Maybe — mixed signals',     chipColor: c.amber  },
  caution: { color: c.amber,  bg: c.amberBg,  label: 'Not great — be careful',    chipColor: c.amber  },
  wait:    { color: c.red,    bg: c.redBg,    label: 'No — better to wait',       chipColor: c.red    },
};

function marketStateFromGex(gexTag: string | null | undefined): string | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return 'Calm and steady';
    case 'POS_GEX_BELOW_FLIP': return 'Recovering after a recent dip';
    case 'NEAR_GAMMA_FLIP':    return 'Unsettled — could move either way';
    case 'NEG_GEX_BELOW_FLIP': return 'Falling, could fall further';
    case 'NEG_GEX_ABOVE_FLIP': return 'Rising, could rise sharply';
    default:                   return null;
  }
}

// Tactical bias label mirroring the SMS tripwire vocabulary. On Notches the
// per-symbol GEX feeds this directly (the page is symbol-scoped), so this row
// is the same thing the spot-tripwire SMS would announce on a bias change for
// the currently-viewed symbol. See ShouldITradeCard.tsx for the equivalent on
// Trade Signals / Stock Signals.
type TacticalBias = { label: string; color: string };
function tacticalBiasFromGex(gexTag: string | null | undefined): TacticalBias | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return { label: 'LONG — sell puts or go long, normal size', color: '#15803d' };
    case 'POS_GEX_BELOW_FLIP': return { label: 'LONG-DIP — sell puts or go long',          color: '#16a34a' };
    case 'NEAR_GAMMA_FLIP':    return { label: 'CHOP — cut new size in half',              color: '#ca8a04' };
    case 'NEG_GEX_BELOW_FLIP': return { label: 'SHORT-DRIFT — no new short puts',          color: '#ea580c' };
    case 'NEG_GEX_ABOVE_FLIP': return { label: 'STAND DOWN — cascade risk',                color: '#dc2626' };
    default:                   return null;
  }
}

function isWaitButLong(safety: SafetyRating, gexTag: string | null | undefined): boolean {
  if (safety !== 'wait' && safety !== 'caution') return false;
  return gexTag === 'POS_GEX_ABOVE_FLIP' || gexTag === 'POS_GEX_BELOW_FLIP';
}

// Direction lean from COT tag. Plain-English label only -- contrarian
// reasoning lives in the whyDirectionLean expansion.
// SPX/SPY/QQQ/IWM have no COT (CFTC tracks futures only); on those symbols
// the direction row simply hides.
function directionFromCot(cotTag: string | null | undefined): string | null {
  switch (cotTag) {
    case 'LM_EXTREME_LONG':    return 'Downward bias';
    case 'LM_STRETCHED_LONG':  return 'Slight downward lean';
    case 'LM_NEUTRAL':         return 'No clear direction';
    case 'LM_STRETCHED_SHORT': return 'Slight upward lean';
    case 'LM_EXTREME_SHORT':   return 'Upward bias';
    default:                   return null;
  }
}

// Combine regime adjustment + macro penalty + skew penalty into one
// 5-step safety rating. Each input nudges the score up or down; the
// composite is mapped to a labeled rating the subscriber sees.
function deriveSafety(
  adjustment: number | null | undefined,
  macroPenalty: number,
  skewPenalty: number,
): SafetyRating {
  let score = 0;
  // Regime adjustment is on a -1..+1 scale (strategy-aware via apply_regime)
  if (adjustment != null) {
    if (adjustment >  0.30) score += 2;
    else if (adjustment >  0.05) score += 1;
    else if (adjustment < -0.30) score -= 2;
    else if (adjustment < -0.05) score -= 1;
  }
  // Macro: -0.30 (extreme) ... 0 (calm)
  if (macroPenalty <= -0.25) score -= 3;
  else if (macroPenalty <= -0.13) score -= 2;
  else if (macroPenalty <= -0.04) score -= 1;
  // Skew penalty mirrors macro magnitude but milder
  if (skewPenalty <= -0.10) score -= 1;

  if (score >=  2) return 'good';
  if (score >=  1) return 'okay';
  if (score >= -1) return 'mixed';
  if (score >= -2) return 'caution';
  return 'wait';
}

function actionForSafety(safety: SafetyRating): string {
  switch (safety) {
    case 'good':    return 'Take new trades at normal size.';
    case 'okay':    return 'Take new trades, but stick to your best setups.';
    case 'mixed':   return 'Trade smaller than usual; only your best setups.';
    case 'caution': return 'Trade quarter-size or skip. Check back later — once this turns favorable, conditions have improved.';
    case 'wait':    return 'Avoid new trades. Check back later — once this turns favorable, conditions have improved.';
  }
}

function presetForSafety(safety: SafetyRating): string {
  switch (safety) {
    case 'good':    return 'Either preset works (Conservative, Balanced, or Aggressive)';
    case 'okay':    return 'Balanced or Conservative';
    case 'mixed':   return 'Conservative only';
    case 'caution': return 'Conservative only, and only if you must trade';
    case 'wait':    return 'Skip today';
  }
}

// Plain-English explanations shown in the expanded "Why?" panel. Kept in
// sync with frontend/src/components/ShouldITradeCard.tsx so subscribers see
// the same wording across Notches + Trade Signals + Stock Signals.
function whyMarketState(gexTag: string | null | undefined): string | null {
  switch (gexTag) {
    case 'POS_GEX_ABOVE_FLIP': return 'Market structure is supportive right now — price moves tend to stay contained.';
    case 'POS_GEX_BELOW_FLIP': return 'The market recently dipped, but the structure tends to pull prices back toward the recent range.';
    case 'NEAR_GAMMA_FLIP':    return 'The market is at a level where moves can accelerate quickly in either direction.';
    case 'NEG_GEX_BELOW_FLIP': return 'Market structure is amplifying downward moves — falls can extend rather than reverse.';
    case 'NEG_GEX_ABOVE_FLIP': return 'Market structure is amplifying upward moves — rallies can extend rather than fade.';
    default: return null;
  }
}

function whyDirectionLean(cotTag: string | null | undefined): string | null {
  switch (cotTag) {
    case 'LM_EXTREME_LONG':    return 'The market is more likely to go DOWN than up. Almost all big traders are already betting on a rise, and historically when the crowd gets this one-sided, prices turn the other way.';
    case 'LM_STRETCHED_LONG':  return 'The market is leaning slightly DOWN. Many big traders are already betting on a rise, and historically when too many traders agree, the opposite move often follows.';
    case 'LM_NEUTRAL':         return 'No clear direction. Big traders are split between bullish and bearish bets — no strong crowd signal either way.';
    case 'LM_STRETCHED_SHORT': return 'The market is leaning slightly UP. Many big traders are already betting on a fall, and historically when too many traders agree, the opposite move often follows.';
    case 'LM_EXTREME_SHORT':   return 'The market is more likely to go UP than down. Almost all big traders are already betting on a fall, and historically when the crowd gets this one-sided, prices turn the other way.';
    default: return null;
  }
}

// Side-by-side question + answer row used in the chip and per-candidate panel
const QARow: React.FC<{ q: string; a: string; aColor?: string; aBold?: boolean }> = ({ q, a, aColor, aBold }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(190px, 36%) 1fr', gap: '0.85rem', padding: '0.32rem 0', borderBottom: `1px solid ${c.border}40` }}>
    <div style={{ fontSize: '0.74rem', color: c.muted }}>{q}</div>
    <div style={{ fontSize: '0.84rem', color: aColor ?? c.text, fontWeight: aBold ? 700 : 500 }}>{a}</div>
  </div>
);

// Snapshot-level "Today's Read" card. Replaces the old RegimeChip.
const TodaysReadCard: React.FC<{
  regime: RegimeDebug | null | undefined;
  macroPenalty: number;
  skewPenalty: number;
}> = ({ regime, macroPenalty, skewPenalty }) => {
  // Don't render anything if the regime data isn't available — this snapshot
  // pre-dates the Phase 4 integration or the engine is disabled.
  if (!regime || regime.mode === 'disabled') return null;

  const marketState = marketStateFromGex(regime.gex_tag);
  const direction   = directionFromCot(regime.cot_tag);
  const safety      = deriveSafety(regime.adjustment, macroPenalty, skewPenalty);
  const action      = actionForSafety(safety);
  const presetHint  = presetForSafety(safety);

  const safetyMeta  = SAFETY_COLOR[safety];
  const whyMarket   = whyMarketState(regime.gex_tag);
  const whyDir      = whyDirectionLean(regime.cot_tag);
  const tactical    = tacticalBiasFromGex(regime.gex_tag);
  const showReadingRule = isWaitButLong(safety, regime.gex_tag);

  return (
    <div
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        padding: '0.7rem 1rem',
        marginBottom: '0.6rem',
      }}
    >
      <details>
        <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.55rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Should I trade right now?</h3>
            <span style={{ fontSize: '0.66rem', color: c.muted }}>▼ details</span>
          </span>
          <span style={{ ...badge(safetyMeta.color, safetyMeta.bg), fontSize: '0.74rem', fontWeight: 700 }}>
            {safetyMeta.label}
          </span>
        </summary>

        <div style={{ marginTop: '0.6rem', borderTop: `1px solid ${c.border}`, paddingTop: '0.5rem' }}>
          <QARow q="What to do"                  a={action} aBold />
          {marketState && <QARow q="Where is the market?" a={marketState} />}
          {direction   && <QARow q="Where is it going?"   a={direction} />}
          {tactical    && <QARow q="Tactical bias right now" a={tactical.label} aColor={tactical.color} aBold />}
          <QARow q="Aggressive or conservative?" a={presetHint} />

          {showReadingRule && (
            <div style={{
              marginTop: '0.55rem',
              fontSize: '0.78rem',
              color: c.text,
              lineHeight: 1.55,
              padding: '0.6rem 0.75rem',
              background: '#0f172a',
              border: `1px solid ${c.amber}`,
              borderRadius: 6,
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong style={{ color: c.amber }}>How to read this:</strong> portfolio risk says wait, but the tactical bias on this symbol is supportive. <strong>Don't open new positions here.</strong> For trades you already hold on this symbol, lean with the tactical bias on management decisions.
              </div>
              <div style={{ fontSize: '0.74rem', color: c.muted, lineHeight: 1.6 }}>
                <div style={{ color: c.text, fontWeight: 600, marginBottom: '0.25rem' }}>Example — short put with bias LONG:</div>
                <div>• <strong>Stops:</strong> give it more room before bailing — recovery is likely</div>
                <div>• <strong>Rolls:</strong> if forced to roll, push the strike DOWN (more cushion)</div>
                <div>• <strong>Profits:</strong> let winners run a bit longer; don't take profit early</div>
                <div style={{ marginTop: '0.35rem', fontStyle: 'italic', color: c.dim }}>
                  If the bias flips to SHORT, do the opposite: tighten stops, close winners faster.
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '0.55rem', fontSize: '0.76rem', color: c.muted, lineHeight: 1.55, padding: '0.5rem 0.65rem', background: c.cardAlt, border: `1px solid ${c.border}`, borderRadius: 6 }}>
            {whyMarket && <div style={{ marginBottom: '0.4rem' }}>{whyMarket}</div>}
            {whyDir    && <div style={{ marginBottom: '0.4rem' }}>{whyDir}</div>}
            {whyDir && (
              <details style={{ marginBottom: '0.4rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.72rem', color: c.muted, fontWeight: 600 }}>
                  How does this work?
                </summary>
                <div style={{ marginTop: '0.35rem', fontSize: '0.76rem', color: c.muted, lineHeight: 1.55 }}>
                  <p style={{ margin: '0 0 0.45rem' }}>
                    If most traders are already betting prices will <strong>fall</strong>, there is almost nobody left to push prices lower. Eventually those traders will need to <strong>buy back</strong> what they sold short — that buying pushes prices UP.
                  </p>
                  <p style={{ margin: '0 0 0.45rem' }}>
                    The same logic in reverse: when most traders are already betting on a <strong>rise</strong>, nobody is left to push prices higher, and longs eventually have to sell to take profits — pushing prices DOWN.
                  </p>
                  <p style={{ margin: 0, color: c.dim, fontStyle: 'italic' }}>
                    This isn't a guarantee. Sometimes the crowd is right and prices keep going their way. But CFTC positioning data going back decades shows that extreme one-sided positioning tends to reverse within weeks more often than not.
                  </p>
                </div>
              </details>
            )}
            {regime.missing && (
              <div style={{ color: c.amber, fontStyle: 'italic' }}>
                Some market signals are not available right now. The read above is based on the data we do have.
              </div>
            )}
            <div style={{ marginTop: '0.3rem', color: c.dim, fontSize: '0.72rem', fontStyle: 'italic' }}>
              This is the current setup. If the market changes while you are in a trade, each trade's "What to watch for" section tells you when to exit.
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};

// Per-candidate "What to watch for" section. Same 5 answers as the snapshot
// chip — repeated inside each trade's expansion so the reader does not have
// to scroll back up to remember the day's context while looking at a trade.
const RegimeContextSection: React.FC<{
  regime: RegimeDebug | null | undefined;
  macroPenalty: number;
  skewPenalty: number;
}> = ({ regime, macroPenalty, skewPenalty }) => {
  if (!regime || regime.mode === 'disabled') return null;
  const safety     = deriveSafety(regime.adjustment, macroPenalty, skewPenalty);
  const safetyMeta = SAFETY_COLOR[safety];
  const action     = actionForSafety(safety);
  const tactical   = tacticalBiasFromGex(regime.gex_tag);
  return (
    <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: '0.65rem', marginTop: '0.65rem' }}>
      <div style={{ fontWeight: 700, color: c.text, marginBottom: '0.45rem', fontSize: '0.78rem' }}>
        What to watch for
      </div>
      <div style={{ fontSize: '0.78rem', color: c.muted, lineHeight: 1.55 }}>
        <div style={{ marginBottom: '0.35rem' }}>
          <strong style={{ color: c.text }}>Today: </strong>
          <span style={{ color: safetyMeta.chipColor, fontWeight: 700 }}>{safetyMeta.label}</span>
          {' — '}{action}.
        </div>
        {tactical && (
          <div style={{ marginBottom: '0.35rem' }}>
            <strong style={{ color: c.text }}>Tactical bias: </strong>
            <span style={{ color: tactical.color, fontWeight: 700 }}>{tactical.label}</span>
          </div>
        )}
        <div style={{ color: c.dim, fontSize: '0.72rem', fontStyle: 'italic' }}>
          The market will change during your hold. If conditions turn against you, the exit framework above (hard stop, trail, time exit) is what protects this trade.
        </div>
      </div>
    </div>
  );
};

// Trade-summary row used inside each delta band. Reads as a plain-English
// trade idea instead of a metric grid:
//   [BADGE] Sell SPX 6675P · exp May 29 (21d) · collect $6.30 · 96% win
//           Take profit $3.15 (50%) · Stop $9.45 · Hold ~10d · Δ -0.04
// "Why" expand has the AI reasoning + full exit framework details.
const PickRow: React.FC<{
  source: PickSource;
  trade: any;
  symbol?: string;
  reasoning?: string;
  expanded: boolean;
  onToggle: () => void;
  preset?: NotchesPreset;
  regime?: RegimeDebug | null;
  macroPenalty?: number;
  skewPenalty?: number;
}> = ({ source, trade, symbol = '', reasoning, expanded, onToggle, preset = 'balanced', regime, macroPenalty = 0, skewPenalty = 0 }) => {
  if (!trade || trade.strike == null) return null;
  const sc = SOURCE_STYLE[source];
  const exitRec = trade.exit_recommendation;
  // Use producer-computed exit if available (WFS picks); fall back to synthesizing
  // identical exit levels for AI picks using their strike/credit/dte/iv.
  const exitForPreset = exitRec?.per_preset?.[preset] ?? synthExitGuidance(trade, preset);

  return (
    <div style={{ borderBottom: `1px solid ${c.border}` }}>
      <div style={{
        padding: '0.65rem 0.9rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <span style={{
          ...badge(sc.color, sc.bg),
          fontSize: '0.68rem',
          minWidth: 76,
          textAlign: 'center',
          flexShrink: 0,
          marginTop: '0.1rem',
        }}>{source}</span>

        <div style={{ flex: 1, minWidth: 280 }}>
          {/* Line 1: trade summary sentence */}
          <div style={{ fontSize: '0.92rem', lineHeight: 1.4, color: c.text }}>
            <strong>Sell {symbol} {trade.strike}P</strong>
            <span style={{ color: c.muted }}> · exp </span>
            <span>{fmtExp(trade.expiration)}</span>
            <span style={{ color: c.muted }}> ({trade.dte}d)</span>
            <span style={{ color: c.muted }}> · collect </span>
            <span style={{ color: c.green, fontWeight: 700 }}>${fmt(trade.credit)}</span>
            <span style={{ color: c.muted }}> · </span>
            <span style={{ fontWeight: 700 }}>{fmt(trade.prob_profit_pct, 1)}%</span>
            <span style={{ color: c.muted }}> win</span>
          </div>

          {/* Line 2: exit guidance + δ */}
          <div style={{ fontSize: '0.78rem', color: c.muted, marginTop: '0.3rem', lineHeight: 1.45 }}>
            {exitForPreset ? (
              <>
                <span style={{ color: c.dim }}>Take profit </span>
                <span style={{ color: c.green, fontWeight: 700 }}>${fmt(exitForPreset.profit_target_credit, 2)}</span>
                <span style={{ color: c.dim }}> ({Math.round(exitForPreset.profit_target_pct * 100)}%)</span>
                <span style={{ color: c.dim }}> · Stop </span>
                <span style={{ color: c.red, fontWeight: 700 }}>${fmt(exitForPreset.hard_stop_credit, 2)}</span>
                <span style={{ color: c.dim }}> ({exitForPreset.hard_stop_multiple}×)</span>
                <span style={{ color: c.dim }}> · Hold </span>
                <span style={{ fontWeight: 700 }}>~{exitForPreset.expected_hold_days}d</span>
                <span style={{ color: c.dim }}> · Δ </span>
                <span>{fmt(trade.delta, 3)}</span>
              </>
            ) : (
              <>
                <span style={{ color: c.dim }}>Δ </span>
                <span>{fmt(trade.delta, 3)}</span>
                <span style={{ color: c.dim }}> · IV </span>
                <span>{fmt(trade.iv_pct, 1)}%</span>
                <span style={{ color: c.dim, fontStyle: 'italic', marginLeft: '0.5rem' }}>
                  (no exit guidance available for this pick)
                </span>
              </>
            )}
          </div>
        </div>

        {(reasoning || exitForPreset) && (
          <button
            onClick={onToggle}
            style={{
              background: expanded ? sc.bg : 'transparent',
              color: sc.color,
              border: `1px solid ${sc.color}`,
              borderRadius: 4,
              padding: '0.3rem 0.65rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              flexShrink: 0,
              marginTop: '0.1rem',
            }}
          >
            {expanded ? '▼ Hide' : '▶ Why'}
          </button>
        )}
      </div>

      {expanded && (
        <div style={{
          padding: '0.7rem 0.9rem 0.95rem',
          background: c.cardAlt,
          fontSize: '0.78rem',
          color: c.muted,
          lineHeight: 1.55,
          borderTop: `1px solid ${c.border}`,
        }}>
          {reasoning && (
            <div style={{ whiteSpace: 'pre-line', marginBottom: exitForPreset ? '0.85rem' : 0 }}>
              {reasoning}
            </div>
          )}
          {exitForPreset && (() => { const universal = exitRec?.universal ?? UNIVERSAL_EXIT_RULES; return (
            <div style={{ borderTop: reasoning ? `1px solid ${c.border}` : 'none', paddingTop: reasoning ? '0.65rem' : 0 }}>
              <div style={{ fontWeight: 700, color: c.text, marginBottom: '0.45rem', fontSize: '0.78rem' }}>
                Exit framework — {PRESET_LABEL[preset]} preset
              </div>
              <div style={{ marginBottom: '0.55rem' }}>
                <strong>Profit target:</strong> close when buy-back price hits ${fmt(exitForPreset.profit_target_credit, 2)} (= {Math.round(exitForPreset.profit_target_pct * 100)}% of the ${fmt(trade.credit, 2)} credit captured). Expected around day {exitForPreset.expected_hold_days}.
              </div>
              <div style={{ marginBottom: '0.55rem' }}>
                <strong>Hard stop:</strong> close at buy-back of ${fmt(exitForPreset.hard_stop_credit, 2)} ({exitForPreset.hard_stop_multiple}× credit) <em>OR</em> if spot drops to ${fmt(exitForPreset.hard_stop_underlying, 2)} <em>OR</em> short delta reaches {universal.hard_stop_delta} — but only when corroborated by ≥{universal.min_corroborators_required} of: high delta, cushion eroded, market moved against, IV breach. Single price breach without corroboration = hold.
              </div>
              <div style={{ marginBottom: '0.55rem' }}>
                <strong>Trail rule:</strong> {exitForPreset.trail_mult_effective}× IV multiplier (ratchet-only — stop tightens as profit accumulates, never widens). With current IV {fmt(trade.iv_pct, 1)}%, base bucket is {exitForPreset.trail_mult_base}×; preset adjustment ×{exitForPreset.trail_mult_adjustment}.
              </div>
              <div style={{ marginBottom: '0.55rem' }}>
                <strong>Smart let-expire:</strong> hold past profit target only when ALL of: short Δ &lt; {universal.smart_let_expire.short_delta_threshold}, market moved favorably ≥{Math.round(universal.smart_let_expire.market_favorable_min_pct * 100)}%, strike &gt;{Math.round(universal.smart_let_expire.deep_otm_threshold_pct * 100)}% OTM, ≤{universal.smart_let_expire.near_expiry_dte} DTE.
              </div>
              <div style={{ color: c.dim, fontSize: '0.7rem', fontStyle: 'italic' }}>
                Time exit: max {exitForPreset.max_hold_days} days regardless. Min hold {universal.min_hold_hours}h to prevent whipsaw.
              </div>
              <RegimeContextSection regime={regime} macroPenalty={macroPenalty} skewPenalty={skewPenalty} />
            </div>
          ); })()}
        </div>
      )}
    </div>
  );
};

const Cell: React.FC<{ label: string; value: string; valueColor?: string; small?: boolean }> = ({
  label, value, valueColor, small,
}) => (
  <div style={{ minWidth: small ? 90 : 60 }}>
    <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: small ? '0.78rem' : '0.85rem', fontWeight: 700, color: valueColor ?? c.text, marginTop: 1 }}>{value}</div>
  </div>
);

const REGIME_STYLE: Record<string, { color: string; bg: string }> = {
  calm:     { color: c.green,  bg: c.greenBg },
  normal:   { color: c.green,  bg: c.greenBg },
  cautious: { color: c.amber,  bg: c.amberBg },
  stressed: { color: c.orange, bg: c.orangeBg },
  elevated: { color: c.orange, bg: c.orangeBg },
  extreme:  { color: c.red,    bg: c.redBg },
  unknown:  { color: c.muted,  bg: 'transparent' },
};

const PRESET_LABEL: Record<NotchesPreset, string> = {
  conservative: 'Conservative',
  balanced: 'Moderate',
  aggressive: 'Aggressive',
};

const ALL_SYMBOLS: NotchesSymbol[] = ['SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'NQ', 'MNQ', 'GC', 'CL'];

// Tiny localStorage-backed useState. Reads on mount, writes on change.
// Tolerates JSON parse failures and storage being unavailable (e.g., private
// mode) — falls back to in-memory state.
function usePersistedState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(key, JSON.stringify(state)); } catch { /* ignore */ }
  }, [key, state]);
  return [state, setState];
}

function getUserSymbols(user: any): NotchesSymbol[] {
  return (['SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'NQ', 'MNQ', 'GC', 'CL'] as NotchesSymbol[]).filter((s) => canAccessSymbol(user, s));
}

// ── Risk Briefings (per-candidate AI brief) ─────────────────────────────────
//
// Generated by notches-risk-briefer (Claude Haiku) on a 9am-ET cron + manual
// refresh. Independent of stress-test.
//
// Each candidate row in the signals table can look up its own briefing via
// keyByStrikeExp(strike, expiration). When the row is expanded, the briefing
// renders inline inside the expansion area. A thin header strip above the
// table shows the load status + Regenerate button.
interface BriefingItem {
  candidateKey: string;
  symbol: string;
  briefing: string;
  riskLevel: 'low' | 'medium' | 'high';
  watchFor: string;
  snapshotTime: string;
  date: string;
  generatedAt: string;
  modelVersion: string;
}

const briefingKey = (strike: number | null | undefined, expiration: string | null | undefined): string => {
  if (strike == null || !expiration) return '';
  return `${Math.round(Number(strike))}P#${expiration}`;
};

const riskLevelColor = (level: string | null | undefined): string =>
  level === 'high' ? c.red : level === 'medium' ? c.amber : level === 'low' ? c.green : c.dim;

// Strike column content for the signals table. `strategy` is the SNAPSHOT-wide
// strategy (`data.strategy` — see notchesService.ts's NotchesSnapshot comment:
// every candidate in a fetched response shares one symbol + one strategy).
// naked_put — today's only live strategy — renders byte-identical to before
// this switch existed: the raw candidate strike, or '—' if missing (C1).
// The four new multi-strategy types (dormant until the Task 8.1 endpoint gap
// is resolved in Phase 9 — see task-8.1/8.2 reports) render all their leg
// strikes with SP/LP/SC/LC labels, per the field conventions from Tasks
// 5.1/5.2/5.3. An unrecognized strategy value falls back to the naked_put
// display (rather than a blank cell) and logs a console.warn, since it
// shouldn't happen in practice.
const renderStrikeLegs = (strategy: string | undefined, candidate: any): React.ReactNode => {
  switch (strategy) {
    case 'naked_put':
      return candidate?.strike ?? '—';
    case 'bull_put_spread':
      return (candidate?.short_strike != null && candidate?.long_strike != null)
        ? `SP ${candidate.short_strike} / LP ${candidate.long_strike}`
        : '—';
    case 'bear_call_spread':
      return (candidate?.short_strike != null && candidate?.long_strike != null)
        ? `SC ${candidate.short_strike} / LC ${candidate.long_strike}`
        : '—';
    case 'jade_lizard':
      return (candidate?.short_put_strike != null && candidate?.short_call_strike != null && candidate?.long_call_strike != null)
        ? `SP ${candidate.short_put_strike} / SC ${candidate.short_call_strike} / LC ${candidate.long_call_strike}`
        : '—';
    case 'iron_condor':
      return (candidate?.put_long_strike != null && candidate?.put_short_strike != null && candidate?.call_short_strike != null && candidate?.call_long_strike != null)
        ? `LP ${candidate.put_long_strike} / SP ${candidate.put_short_strike} / SC ${candidate.call_short_strike} / LC ${candidate.call_long_strike}`
        : '—';
    default:
      // Unknown/missing strategy — shouldn't happen, but fall back to the
      // naked_put display rather than shipping a blank card.
      // eslint-disable-next-line no-console
      console.warn(`[NotchesPage] Unknown signal strategy "${strategy}" — falling back to naked_put strike display`);
      return candidate?.strike ?? '—';
  }
};

// Hook: fetch briefings for a (symbol, snapshotTime) pair + expose refresh +
// a lookup map. snapshotTime is optional; when null the API returns the
// latest snapshot's briefings (used on first load before a snapshot is
// explicitly picked).
function useRiskBriefings(token: string | null, symbol: NotchesSymbol, snapshotTime: string | null) {
  const [briefings, setBriefings] = useState<BriefingItem[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  // The snapshot these briefings ACTUALLY belong to (echoed by the API).
  // Distinct from generatedAt - the snapshot is what the briefings analyze;
  // generatedAt is when Claude wrote the analysis.
  const [actualSnapshotTime, setActualSnapshotTime] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await notchesService.getRiskBriefings(token, symbol, snapshotTime);
      setBriefings(r.briefings || []);
      setGeneratedAt(r.generatedAt);
      setActualSnapshotTime(r.snapshotTime);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load risk briefings');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    if (!token) return;
    setRefreshing(true);
    setErr(null);
    try {
      await notchesService.refreshRiskBriefings(token, symbol);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, symbol, snapshotTime]);

  const lookup = useMemo(() => {
    const m = new Map<string, BriefingItem>();
    for (const b of briefings) m.set(b.candidateKey, b);
    return m;
  }, [briefings]);

  return { briefings, lookup, generatedAt, actualSnapshotTime, loading, refreshing, err, refresh };
}

// Thin header strip above the signals table.
const RiskBriefingsHeader: React.FC<{
  symbol: NotchesSymbol;
  count: number;
  snapshotTime: string | null;
  generatedAt: string | null;
  refreshing: boolean;
  err: string | null;
  onRefresh: () => void;
}> = ({ symbol, count, snapshotTime, generatedAt, refreshing, err, onRefresh }) => (
  <div style={{
    background: c.card, border: `1px solid ${c.border}`,
    borderLeft: `3px solid ${c.blue}`,
    borderRadius: 6, padding: '0.45rem 0.85rem', marginBottom: '0.6rem',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '0.5rem', flexWrap: 'wrap',
  }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
      <strong>AI Risk Briefings for {symbol}</strong>
      <span style={{ color: c.muted }}>
        {count > 0 ? `${count} ${symbol} picks have inline briefings — expand any matching row to read` : `No ${symbol} briefings yet`}
      </span>
      {/* Both timestamps dropped from this header per Bobby 2026-05-17:
          snapshot time is redundant with the snapshot selector chips above,
          and generatedAt caused confusion when it didn't match the snapshot.
          Per-brief timestamps remain on each InlineBriefingCard. */}
      {err && <span style={{ color: c.red, fontSize: '0.72rem' }}>{err}</span>}
    </div>
    <button
      onClick={onRefresh}
      disabled={refreshing}
      style={{
        background: 'transparent', color: c.blue, border: `1px solid ${c.blue}`,
        padding: '3px 10px', borderRadius: 4, fontSize: '0.72rem',
        cursor: refreshing ? 'wait' : 'pointer',
      }}
      title="Regenerate the AI briefings using the current snapshot"
    >
      {refreshing ? 'Regenerating...' : 'Regenerate'}
    </button>
  </div>
);

// Inline briefing card rendered inside an expanded row.
const InlineBriefingCard: React.FC<{ b: BriefingItem }> = ({ b }) => (
  <div style={{
    background: c.bg, border: `1px solid ${c.border}`,
    borderLeft: `3px solid ${riskLevelColor(b.riskLevel)}`,
    borderRadius: 6, padding: '0.6rem 0.75rem', marginBottom: '0.6rem',
    fontSize: '0.82rem',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
      <strong style={{ fontSize: '0.85rem' }}>AI Risk Briefing</strong>
      <span style={{
        color: riskLevelColor(b.riskLevel), fontSize: '0.62rem',
        fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      }}>{b.riskLevel} risk</span>
    </div>
    <div style={{ color: c.text, lineHeight: 1.45, marginBottom: 6 }}>
      {b.briefing}
    </div>
    {b.watchFor && (
      <div style={{ color: c.muted, fontSize: '0.74rem', fontStyle: 'italic' }}>
        Watch: {b.watchFor}
      </div>
    )}
  </div>
);

// Group filter from the Notches dashboard tiles. ?group=index hides ES/MES;
// ?group=futures hides SPX/SPY. No param = all symbols visible (legacy nav).
const GROUP_SYMBOLS: Record<string, NotchesSymbol[]> = {
  index:   ['SPX', 'SPY'],
  futures: ['ES', 'MES'],
};

// Task 8.2: strategy filter chip row, above the signal table. Value is the
// internal strategy string written to notches-signals-v1 / algo-signals-prod;
// label is what the chip displays. 'all' is the reset state (no ?strategy=).
//
// IMPORTANT (see task-8.1-report.md): notches-signals-v1 -- the only data
// source behind this page's fetch (notchesService.getSignals -> GET
// /notches/signals) -- currently only ever contains strategy='naked_put'
// records. The three new-strategy Phase 4 generators write to a separate
// algo-signals-prod table read by a different endpoint (getSignals.js), not
// this one. So today, only the "All" and "Naked puts" chips will ever have
// signals; the other four render disabled until that gap is resolved
// (Bobby's Phase 9 decision — not addressed by this task).
const STRATEGY_CHIPS: { value: string; label: string }[] = [
  { value: 'all',              label: 'All' },
  { value: 'naked_put',        label: 'Naked puts' },
  { value: 'bull_put_spread',  label: 'Put spreads' },
  { value: 'bear_call_spread', label: 'Call spreads' },
  { value: 'jade_lizard',      label: 'Jade lizards' },
  { value: 'iron_condor',      label: 'Iron condors' },
];

// Notches mixed-table plan Task 2.4: per-strategy badge label + color for the
// STRAT column. Keys match the snake_case values used in DDB `strategy` fields
// and in the mixed-pool `_strategy` annotation (Task 2.3). Colors reuse the
// existing WFS palette so nothing new is introduced here.
const NOTCHES_STRATEGY_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  naked_put:        { label: 'PUT',  color: c.green,  bg: c.greenBg  },
  bull_put_spread:  { label: 'BPS',  color: c.green,  bg: c.greenBg  },
  bear_call_spread: { label: 'BCS',  color: c.red,    bg: c.redBg    },
  jade_lizard:      { label: 'JADE', color: c.amber,  bg: c.amberBg  },
  iron_condor:      { label: 'IC',   color: c.blue,   bg: c.blueBg   },
};

export const NotchesPage: React.FC = () => {
  const { token, user, updateProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const group = searchParams.get('group') || '';
  const groupSymbols = GROUP_SYMBOLS[group];
  const allUserSymbols = useMemo(() => getUserSymbols(user), [user]);
  // When a group filter is active, intersect with the user's per-symbol subs
  // so the tab strip only shows symbols both in-group AND owned by the user.
  const userSymbols = useMemo(() => (
    groupSymbols
      ? allUserSymbols.filter((s) => groupSymbols.includes(s))
      : allUserSymbols
  ), [allUserSymbols, groupSymbols]);
  // Initial symbol: prefer the ?symbol= param, else first in the filtered list.
  const initialSymbol = (searchParams.get('symbol') || '').toUpperCase() as NotchesSymbol;
  const [symbol, setSymbol] = useState<NotchesSymbol>(() => (
    userSymbols.includes(initialSymbol) ? initialSymbol : (userSymbols[0] ?? 'SPX')
  ));

  // Task 8.2: strategy filter chip row. Derived directly from the URL (mirrors
  // the `group` pattern above) rather than mirrored into its own useState, so
  // the URL stays the single source of truth. Missing/unrecognized param ==
  // 'all' == no filter == current (pre-8.2) behavior, satisfying C1.
  const strategyFilter = searchParams.get('strategy') || 'all';
  const setStrategyFilter = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('strategy');
      else next.set('strategy', value);
      return next;
    }, { replace: true });
  };

  const [refreshTick, setRefreshTick] = useState(0);
  const [data, setData] = useState<NotchesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(() => new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set());
  const [showAlternates, setShowAlternates] = useState<boolean>(false);
  type SortKey = 'rank' | 'dte' | 'strike' | 'delta' | 'credit' | 'cushion' | 'win' | 'rr' | 'iv' | 'score' | 'target' | 'stop' | 'conv';
  const [sortKey, setSortKey] = useState<SortKey>('dte');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Filter knobs — persisted to localStorage so the user's slider/toggle
  // choices survive page reloads and re-logins (was: reset to defaults
  // every visit).
  const [maxDte, setMaxDte]         = usePersistedState<number>('notches:maxDte', 120);
  const [showWfs, setShowWfs]       = usePersistedState<boolean>('notches:showWfs', true);
  const [showClaude, setShowClaude] = usePersistedState<boolean>('notches:showClaude', true);
  const [showGpt, setShowGpt]       = usePersistedState<boolean>('notches:showGpt', true);
  const [pulseOpen, setPulseOpen]   = useState<boolean>(false);
  const [chatOpen, setChatOpen]     = useState<boolean>(false);

  // Globally-fetched Macro Backdrop (freshest macro across all symbols).
  // Workstream (B) fix 2026-06-23: previously the panel rendered each symbol's
  // own bundled macro from data.macro, which drifted between generators (SPX
  // and GC showed different macro at the same UI moment). Now we fetch the
  // freshest snapshot via /market/regime and render that consistently.
  const [globalMacro, setGlobalMacro]       = useState<MacroBlock | null>(null);
  const [globalMacroAsOf, setGlobalMacroAsOf] = useState<string | null>(null);

  // V2-style Current/History tabs + snapshot selector
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [histDate, setHistDate] = useState<string>(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  );
  const [selectedSnapshotTime, setSelectedSnapshotTime] = useState<string | null>(null);

  // Risk briefings lookup map keyed by `${strike}P#${expiration}` — used inline
  // per row. Pinned to the user's selected snapshot so the briefings shown
  // match the candidates on screen (briefer runs per snapshot; without this
  // the header would always show the latest snapshot's briefings even when
  // viewing an older snapshot).
  const briefingsApi = useRiskBriefings(token, symbol, selectedSnapshotTime);

  // Scoring preset is persisted on the user record (default 'conservative'
  // for new users) so it can be read by SMS / notification Lambdas server-side.
  // We mirror it into local state for instant UI response, but every change
  // is also pushed via updateProfile so the user record is the source of truth.
  const [preset, setPresetLocal] = useState<NotchesPreset>(
    (user?.notchesPreset as NotchesPreset) || 'conservative'
  );
  useEffect(() => {
    if (user?.notchesPreset) setPresetLocal(user.notchesPreset as NotchesPreset);
  }, [user?.notchesPreset]);
  const setPreset = (next: NotchesPreset) => {
    setPresetLocal(next);
    // Fire-and-forget — failure shouldn't block the UI. AuthContext.updateProfile
    // already updates user state on 200.
    updateProfile({ notchesPreset: next }).catch((e) => {
      console.error('Failed to persist notchesPreset:', e);
    });
  };

  useEffect(() => {
    if (userSymbols.length > 0 && !userSymbols.includes(symbol)) {
      setSymbol(userSymbols[0]);
    }
  }, [userSymbols, symbol]);

  // Reset snapshot selection when the symbol or tab/date context changes —
  // a snapshot_time from a different (symbol, date) is meaningless.
  useEffect(() => {
    setSelectedSnapshotTime(null);
  }, [symbol, tab, histDate]);

  // Fetch the freshest macro across all symbols (workstream B 2026-06-23).
  // 5-min cache lives inside marketRegimeService; this fires once per page load.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await marketRegimeService.get(token);
        if (cancelled) return;
        if (r.macro) {
          setGlobalMacro(r.macro);
          setGlobalMacroAsOf(r.macro_as_of || null);
        }
      } catch (e) {
        // Silent fail -- the panel falls back to the per-symbol bundled macro
        console.warn('global macro fetch failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshTick]);

  useEffect(() => {
    if (!token) return;
    if (!userSymbols.includes(symbol)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // V2-style: 'current' tab pulls today (no date param); 'history' tab pulls histDate.
    const dateParam = tab === 'history' ? histDate : undefined;
    const snapshotParam = selectedSnapshotTime || undefined;

    (async () => {
      try {
        // Notches mixed-table plan Task 2.2: always request the per-strategy
        // snapshot map. When present, `data.strategies` lets the mixed pool
        // (Task 2.3) show candidates from all 4 strategies interleaved on the
        // same page. Absent-param callers see the exact same primary response
        // shape as before (backward-compat verified in test-get-notches-signals-
        // strategy-filter.js Task 1.1 tests).
        let resp = await notchesService.getSignals(token, symbol, preset, dateParam, snapshotParam, 'all-strategies');
        // V2-style fallback: if 'current' tab returned empty (today's snapshot
        // doesn't exist yet — pre-market open, weekend, or holiday), retry with
        // yesterday so the user always lands on the most recent real data.
        if (tab === 'current' && !snapshotParam && isEmpty(resp)) {
          const yd = new Date(Date.now() - 86400000)
            .toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
          resp = await notchesService.getSignals(token, symbol, preset, yd, undefined, 'all-strategies');
        }
        if (!cancelled) setData(resp);
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.response?.data?.error || e?.message || 'Failed to load Notches signals';
        setError(msg);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, symbol, preset, tab, histDate, selectedSnapshotTime, userSymbols, refreshTick]);

  // Notches mixed-table plan Task 2.3: mixed candidate pool.
  //
  // Combines the primary snapshot's candidates (naked_put by default —
  // `data.candidates` + `data.strategy`) with the per-strategy snapshots
  // under `data.strategies` (populated by the ?include=all-strategies mode
  // added in Task 1.1). Each row gets a `_strategy` annotation so the table
  // (Task 2.4) can render a STRAT badge and route renderStrikeLegs to the
  // right leg-shape.
  //
  // Dedup rule: the primary snapshot's candidates always win over any
  // strategies-map entry for the same strategy (they're the same rows from
  // the API's POV, just rendered from a different code path). E.g. when
  // `data.strategy === 'naked_put'` AND `data.strategies.naked_put` is also
  // populated, we take primary and skip strategies.naked_put.
  const mixedCandidates: NotchesCandidate[] = useMemo(() => {
    if (!data || isEmpty(data)) return [];
    const primary = (data.candidates || []).map((cd) => ({ ...cd, _strategy: data.strategy || 'naked_put' }));
    const seenStrategies = new Set<string>([data.strategy || 'naked_put']);
    const strategies = data.strategies || {};
    const extras: NotchesCandidate[] = [];
    for (const [stratKey, snap] of Object.entries(strategies)) {
      if (!snap) continue;
      if (seenStrategies.has(stratKey)) continue;
      for (const cd of (snap.candidates || [])) {
        extras.push({ ...cd, _strategy: stratKey });
      }
      seenStrategies.add(stratKey);
    }
    // Preset gate for all 4 spread strategies (BCS/BPS/JL/IC). Extended
    // 2026-09-05 as part of the spread preset retrofit (spec:
    // docs/superpowers/specs/2026-09-04-spread-preset-retrofit-design.md).
    // Original 2026-08-27 gate covered only bear_call_spread; all four
    // spread runners now emit eligible_presets via spread_preset_framework,
    // so filtering by preset is consistent across the strategy family.
    // Candidates without eligibility info pass through (defensive: don't
    // drop rows just because the field is missing on an older DDB row).
    // Naked_put candidates are unfiltered here — naked_put keeps its
    // existing sort-only preset semantic where all rows show but rank by
    // scores[preset].
    const SPREAD_STRATEGIES = new Set([
      'bear_call_spread',
      'bull_put_spread',
      'jade_lizard',
      'iron_condor',
    ]);
    return [...primary, ...extras].filter((cd) => {
      if (!SPREAD_STRATEGIES.has((cd as any)._strategy)) return true;
      const el = cd.eligible_presets;
      if (!Array.isArray(el) || el.length === 0) return true;
      return el.includes(preset);
    });
  }, [data, preset]);

  // DTE-filtered mixed pool. Strategy chip filter is deliberately NOT applied
  // here — kept separate below so chip signal-counts can be computed against
  // the pre-strategy-filter pool (see chipCounts).
  const dteFilteredCandidates: NotchesCandidate[] = useMemo(() => {
    return mixedCandidates.filter((cd) => (cd.dte ?? 999) <= maxDte);
  }, [mixedCandidates, maxDte]);

  // Client-side strategy filter, applied on top of the DTE filter. Filter
  // key is the `_strategy` annotation attached by mixedCandidates. `all` is
  // pass-through so default-state rendering is unchanged from pre-plan
  // behavior (aside from now also including non-naked_put strategies in the
  // 'all' pool where they exist).
  const candidates: NotchesCandidate[] = useMemo(() => {
    if (strategyFilter === 'all') return dteFilteredCandidates;
    return dteFilteredCandidates.filter((cd) => (cd as any)._strategy === strategyFilter);
  }, [dteFilteredCandidates, strategyFilter]);

  // Signal counts per chip, used to disable chips with nothing to show for
  // the currently-loaded snapshot. Counts are per-strategy against the
  // DTE-filtered mixed pool.
  const chipCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = { all: dteFilteredCandidates.length };
    for (const chip of STRATEGY_CHIPS) {
      if (chip.value === 'all') continue;
      counts[chip.value] = dteFilteredCandidates.filter((cd) => (cd as any)._strategy === chip.value).length;
    }
    return counts;
  }, [dteFilteredCandidates]);

  // Flat-table rows — one per canonical DTE bucket (the AI prompt horizons).
  // Each row has:
  //   - WFS top pick at that DTE (highest preset-scored candidate within ±2 days)
  //   - Claude pick at that DTE (from best_per_horizon)
  //   - ChatGPT pick at that DTE
  //   - AI-match flags: full ✓ if same strike (within 1% of spot) AND same DTE
  //     (±1d); partial ~ if same DTE but different strike; null if AI didn't
  //     pick at this DTE
  //
  // Default = 9 DTE buckets: AI's natural horizons.
  // If user toggles "show alternates", we ALSO surface extra WFS candidates
  // and AI-specific rows as separate sub-rows on expand.
  type AiMatch = {
    pick: any | null;
    strikeMatch: boolean;
    dteMatch: boolean;
  };
  type TableRow = {
    dte: number;
    strategy: string | null;      // 2026-08-29: pair-with-dte key (React reconciliation)
    rowKey: string;               // `${dte}|${strategy}` — unique per row, used for React key + rank lookup
    wfs: NotchesCandidate | null;
    wfsScore: number | null;
    wfsReasoning: string;
    wfsAlternates: NotchesCandidate[];
    claude: AiMatch;
    gpt: AiMatch;
  };

  const tableRows: TableRow[] = useMemo(() => {
    const hasData = data && !isEmpty(data);
    if (!hasData) return [];
    const spot = data.spot_price || 1;
    // Producer now writes `best_per_band` (4 entries — one per delta band).
    // Older records still carry `best_per_horizon`. Read both; merge for display.
    const aiPicks = (analysis: any): any[] => {
      const arr: any[] = [];
      const bpb = analysis?.best_per_band;
      if (Array.isArray(bpb)) arr.push(...bpb);
      const bph = analysis?.best_per_horizon;
      if (Array.isArray(bph)) arr.push(...bph);
      // Drop zero placeholder picks: gpt-4.1 / Sonnet 5.5 sometimes return
      // {strike: 0, dte: 0, ...} for an empty band instead of null.
      return arr.filter((p) => p && p.strike != null && p.dte != null
        && Number(p.strike) > 0 && Number(p.dte) > 0);
    };
    // Notches mixed-table plan Task 2.5 hotfix: AI picks (Claude, GPT) are
    // always naked_put picks from the primary snapshot's AI analysis blocks.
    // When the user filters to a specific non-'all' strategy (e.g. Call
    // spreads), AI picks would:
    //   (a) surface DTE buckets where no candidate of the filtered strategy
    //       exists — those rows fall back to primary=aiPick and render as
    //       PUT badges under the strategy filter chip, which is wrong;
    //   (b) get compared against spread candidates in matchFor, producing
    //       nonsensical "~" strike-mismatch cells.
    // Both of these regressed as of 2026-08-27 beta deploy. Fix: when a
    // strategy filter is active, drop AI picks entirely so the DTE list is
    // driven solely by matching candidates.
    const claudePicks: any[] = strategyFilter === 'all' ? aiPicks((data as any).claude_analysis) : [];
    const gptPicks: any[]    = strategyFilter === 'all' ? aiPicks((data as any).chatgpt_analysis) : [];

    // Collect every distinct (DTE, strategy) pair present in the pool
    // — plus AI picks (naked_put only). Emitting one row per pair (2026-08-29)
    // is what makes the "All" chip actually show every strategy: under the old
    // "one row per DTE" design, BCS beat JL/IC at every DTE bucket in the
    // 35-50 window and JL/IC were invisible on "All". Per-strategy chip views
    // ('Naked puts', 'Jade lizards', ...) already filter `candidates` upstream
    // (line 1147), so this change only affects the mixed 'All' view.
    type DteKey = { dte: number; strategy: string | null };
    const keys: DteKey[] = [];
    const seen = new Set<string>();
    const add = (dte: number, strategy: string | null) => {
      const k = `${dte}|${strategy ?? ''}`;
      if (seen.has(k)) return;
      seen.add(k);
      keys.push({ dte, strategy });
    };
    for (const cd of candidates) {
      if (cd.dte == null) continue;
      add(cd.dte, (cd as any)._strategy ?? null);
    }
    // AI picks are always naked_put picks (see the strategyFilter=='all'
    // gate above where they're populated). Route them to the naked_put row
    // at each DTE so match cells line up with the right side of the table.
    for (const p of claudePicks) if (p?.dte != null) add(p.dte, 'naked_put');
    for (const p of gptPicks)    if (p?.dte != null) add(p.dte, 'naked_put');

    // AI pick matches the row's DTE exactly (no fuzzy bucket logic).
    const matchFor = (wfsTop: NotchesCandidate | null, aiPicks: any[], targetDte: number): AiMatch => {
      const best = aiPicks.find((p) => p?.dte === targetDte && p.strike != null) || null;
      if (!best) return { pick: null, strikeMatch: false, dteMatch: false };
      if (!wfsTop) return { pick: best, strikeMatch: false, dteMatch: true };
      const strikeDiff = Math.abs(best.strike - wfsTop.strike) / spot;
      return {
        pick: best,
        strikeMatch: strikeDiff <= 0.01,
        dteMatch: true,
      };
    };

    // Filter by LIVE DTE (today → exp) instead of stored DTE so the slider
     // value matches the column display. Producer-stored `dte` is snapshot-
     // relative and goes stale after weekends/holidays.
    const liveDteForStored = (targetDte: number): number => {
      const cd = candidates.find((c) => c.dte === targetDte)
              ?? claudePicks.find((p) => p?.dte === targetDte)
              ?? gptPicks.find((p) => p?.dte === targetDte);
      const ld = cd?.expiration ? liveDte(cd.expiration) : null;
      return ld != null ? ld : targetDte;
    };

    return keys
      .filter((k) => liveDteForStored(k.dte) <= maxDte)
      .sort((a, b) => a.dte - b.dte || String(a.strategy).localeCompare(String(b.strategy)))
      .map(({ dte: targetDte, strategy: targetStrat }) => {
        // Candidates at THIS DTE for THIS strategy (or all strategies when
        // targetStrat is null, e.g. an AI-only row where no WFS pick exists).
        const atDte = candidates.filter((cd) =>
          cd.dte === targetDte
          && (targetStrat == null || (cd as any)._strategy === targetStrat));
        atDte.sort((a, b) => {
          const sa = a.scores?.[preset] ?? Number.NEGATIVE_INFINITY;
          const sb = b.scores?.[preset] ?? Number.NEGATIVE_INFINITY;
          return sb - sa;
        });
        const wfs = atDte[0] ?? null;
        const wfsScore = wfs?.scores?.[preset] ?? null;
        const wfsAlternates = atDte.slice(1, 4);
        const wfsReasoning = wfs
          ? `Highest-scored WFS candidate at ${targetDte} DTE under the ${PRESET_LABEL[preset]} preset. Δ ${fmt(wfs.delta, 3)}, ${fmt(wfs.points_from_atm, 0)} pts from spot, ${fmt(wfs.prob_profit_pct, 1)}% probability of profit. You collect $${fmt(wfs.credit, 2)} (~$${fmt(wfs.credit_per_day, 3)}/day). Scored highest across credit velocity, cushion, theta-efficiency, vega exposure, and liquidity.`
          : '';

        // AI picks match only against naked_put rows (Claude + GPT prompt the
        // naked_put snapshot). Skip AI matching on non-naked_put rows to avoid
        // false "~" mismatch cells that compared spread strikes against put strikes.
        const isNakedPutRow = targetStrat === 'naked_put' || targetStrat == null;
        return {
          dte: targetDte,
          strategy: targetStrat,
          rowKey: `${targetDte}|${targetStrat ?? ''}`,
          wfs,
          wfsScore,
          wfsReasoning,
          wfsAlternates,
          claude: isNakedPutRow ? matchFor(wfs, claudePicks, targetDte) : { pick: null, strikeMatch: false, dteMatch: false },
          gpt:    isNakedPutRow ? matchFor(wfs, gptPicks, targetDte)    : { pick: null, strikeMatch: false, dteMatch: false },
        };
      })
      .filter((row) => row.wfs || row.claude.pick || row.gpt.pick);
  }, [data, candidates, preset, maxDte, strategyFilter]);

  // Sorted view per current sortKey/sortDir.
  // Rank rows by WFS score under the current preset. Rank 1 = highest score.
  // Rows with no WFS pick (AI-only) don't get a rank. The count denominator
  // is the number of WFS-scored rows for this preset — so users see "3/9",
  // meaning "third-best of nine ranked candidates."
  const { rankByRow, rankTotal } = useMemo(() => {
    // Key rank by rowKey (dte, strategy) — with the mixed-pool design, two
    // rows can share a DTE (naked_put at 34d + jade_lizard at 34d). A dte-only
    // Map collapses them; rank display then shows the same rank on both.
    // Coerce via Number(): the futures gzip path once serialized Decimals as
    // strings (2026-09-09), and a strict typeof filter silently dropped every
    // JL/IC row from ranking.
    const scored = tableRows
      .map((r) => {
        const raw = r.wfs?.scores?.[preset];
        const n = typeof raw === 'number' ? raw : Number(raw);
        return r.wfs && Number.isFinite(n) ? { rowKey: r.rowKey, score: n } : null;
      })
      .filter((x): x is { rowKey: string; score: number } => x !== null)
      .sort((a, b) => b.score - a.score);
    const map = new Map<string, number>();
    scored.forEach((s, i) => map.set(s.rowKey, i + 1));
    return { rankByRow: map, rankTotal: scored.length };
  }, [tableRows, preset]);

  const sortedRows = useMemo(() => {
    const rows = [...tableRows];
    const valFor = (r: TableRow): number => {
      // Use whichever pick is shown in the row (WFS first, else AI fallback).
      const primary: any = r.wfs ?? r.claude.pick ?? r.gpt.pick ?? null;
      if (!primary) return -Infinity;
      switch (sortKey) {
        case 'rank':    return rankByRow.get(r.rowKey) ?? Number.POSITIVE_INFINITY;
        case 'dte':     return r.dte;
        case 'strike':  return primary.strike ?? 0;
        case 'delta':   return Math.abs(primary.delta ?? 0);
        case 'credit':  return primary.credit ?? 0;
        case 'cushion': return primary.points_from_atm ?? 0;
        case 'win':     return primary.prob_profit_pct ?? 0;
        case 'rr':      return primary.sharpe_annualized ?? 0;
        case 'iv':      return primary.iv_pct ?? 0;
        case 'score':   return (r.wfs?.scores?.[preset]) ?? 0;
        case 'target': {
          const exit = r.wfs
            ? r.wfs.exit_recommendation?.per_preset?.[preset]
            : synthExitGuidance(primary, preset);
          return exit?.profit_target_credit ?? 0;
        }
        case 'stop': {
          const exit = r.wfs
            ? r.wfs.exit_recommendation?.per_preset?.[preset]
            : synthExitGuidance(primary, preset);
          return exit?.hard_stop_credit ?? 0;
        }
        case 'conv': {
          // Convergence sort score: STRONG=4, MEDIUM=3, MIXED=2, SOLO=1, NONE=0.
          // SOLO means only one source picked here — convergence isn't measurable.
          const w = r.wfs?.strike;
          const cl = r.claude.pick?.strike;
          const gp = r.gpt.pick?.strike;
          const inputs = [w, cl, gp].filter((x) => x != null).length;
          if (inputs === 0) return 0;
          if (inputs === 1) return 1;
          const set = new Set<number>();
          if (w != null) set.add(w);
          if (cl != null) set.add(cl);
          if (gp != null) set.add(gp);
          if (inputs === 3 && set.size === 1) return 4;
          if (set.size < inputs) return 3;
          return 2;
        }
      }
    };
    rows.sort((a, b) => {
      const av = valFor(a);
      const bv = valFor(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [tableRows, sortKey, sortDir, preset]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'dte' || k === 'strike' ? 'asc' : 'desc'); }
  };

  const toggleRowExpand = (rowKey: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  };

  const toggleReason = (key: string) => {
    setExpandedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ── Page-level shells ──────────────────────────────────────────────────────

  // No subscriptions at all: friendly empty page
  if (userSymbols.length === 0) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: c.text }}>
          <h2 style={{ marginBottom: '0.75rem' }}>Notches</h2>
          <p style={{ color: c.muted, maxWidth: 500, margin: '0 auto 1.5rem' }}>
            Notches signals are gated per symbol. Subscribe to SPX, SPY, ES, or MES to see signals here.
          </p>
          <a href="/upgrade" style={{
            display: 'inline-block',
            backgroundColor: c.purple,
            color: '#fff',
            padding: '0.75rem 1.5rem',
            textDecoration: 'none',
            borderRadius: 8,
            fontWeight: 'bold',
            fontSize: 14,
          }}>View subscriptions</a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: '1.5rem',
        color: c.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>

        {/* Header — title + action buttons (Market Pulse / Refresh / Ask AI placeholder) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Notches</h1>
            <div style={{ color: c.muted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
              WFS model + independent Claude &amp; ChatGPT picks per DTE. Tap "Why" on any row for the reasoning.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setPulseOpen(true)}
              style={{
                padding: '0.5rem 1rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.82rem',
                background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
              }}
            >Market Pulse</button>
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              style={{
                padding: '0.5rem 1rem', borderRadius: 6, border: `1px solid ${c.border}`,
                background: 'transparent', color: c.muted, cursor: 'pointer', fontSize: '0.82rem',
              }}
            >Refresh</button>
            {data && !isEmpty(data) && (
              <button
                onClick={() => setChatOpen(!chatOpen)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.82rem',
                  background: chatOpen ? '#7c3aed' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  color: '#fff',
                  boxShadow: chatOpen ? 'none' : '0 2px 8px rgba(139,92,246,0.4)',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <span style={{ fontSize: '1rem' }}>&#128172;</span>
                {chatOpen ? 'Close Chat' : 'Ask AI'}
              </button>
            )}
          </div>
        </div>

        {/* Symbol tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          {userSymbols.map((s) => {
            const active = symbol === s;
            return (
              <button
                key={s}
                onClick={() => setSymbol(s)}
                style={{
                  padding: '0.5rem 1.1rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  border: `1px solid ${active ? c.blue : c.border}`,
                  background: active ? c.blue : c.card,
                  color: active ? '#fff' : c.muted,
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 120ms',
                }}
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Current / History tabs (mirrors V2 pattern) */}
        <div style={{ borderBottom: `1px solid ${c.border}`, marginBottom: '1rem', display: 'flex' }}>
          {(['current', 'history'] as const).map((t) => {
            const active = tab === t;
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '0.6rem 1.25rem', cursor: 'pointer', border: 'none',
                  borderBottom: active ? `2px solid ${c.purple}` : '2px solid transparent',
                  background: 'transparent',
                  color: active ? c.text : c.muted,
                  fontWeight: active ? 600 : 400,
                  fontSize: '0.9rem',
                  textTransform: 'capitalize',
                }}
              >{t}</button>
            );
          })}
        </div>

        {/* History date picker (only in History tab) */}
        {tab === 'history' && (
          <div style={{
            background: c.card, borderRadius: 8, border: `1px solid ${c.border}`,
            padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
            marginBottom: '1rem', flexWrap: 'wrap',
          }}>
            <label style={{ fontSize: '0.85rem', color: c.muted }}>Date:</label>
            <input
              type="date"
              value={histDate}
              onChange={(e) => setHistDate(e.target.value)}
              style={{
                background: c.cardAlt, color: c.text, border: `1px solid ${c.border}`,
                borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.85rem',
              }}
            />
          </div>
        )}

        {/* Snapshot selector chips — appears when multiple snapshots exist for the day */}
        {data && data.snapshots && data.snapshots.length > 1 && (
          <div style={{
            background: c.card, borderRadius: 8, border: `1px solid ${c.border}`,
            padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center',
            gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem',
          }}>
            <span style={{ fontSize: '0.78rem', color: c.muted }}>Snapshot:</span>
            {data.snapshots.map((s) => {
              const active = !isEmpty(data) && data.snapshot_time === s.snapshot_time;
              return (
                <button
                  key={s.snapshot_time}
                  onClick={() => setSelectedSnapshotTime(s.snapshot_time)}
                  style={{
                    padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none',
                    fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                    background: active ? c.purple : c.cardAlt,
                    color: active ? '#fff' : c.muted,
                  }}
                >{fmtDate(s.collected_at || s.snapshot_time)}</button>
              );
            })}
          </div>
        )}

        {loading && <div style={{ color: c.muted }}>Loading…</div>}
        {error && (
          <div style={{
            color: c.red,
            padding: '0.75rem 1rem',
            background: c.redBg,
            border: `1px solid ${c.red}`,
            borderRadius: 6,
            fontSize: '0.85rem',
          }}>
            {error}
          </div>
        )}

        {data && isEmpty(data) && (
          <div style={{
            padding: '1.25rem 1.5rem',
            color: c.muted,
            background: c.card,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            fontSize: '0.88rem',
          }}>
            {data.message}
          </div>
        )}

        {data && !isEmpty(data) && (
          <>
            {/* Snapshot metadata strip — annotated with user-friendly explanations */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.85rem',
              padding: '0.65rem 1rem',
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              marginBottom: '0.6rem',
              fontSize: '0.8rem',
            }}>
              {/* Snapshot stat only when the chip bar isn't already showing it */}
              {(!data.snapshots || data.snapshots.length <= 1) && (
                <Stat
                  label="Snapshot"
                  value={fmtDate(data.collected_at || data.snapshot_time)}
                  explain="When this market data was captured (Eastern time)."
                />
              )}
              <Stat
                label="Spot"
                value={fmt(data.spot_price)}
                explain="Current price of the underlying when this snapshot was taken."
              />
              <Stat
                label="Options Analyzed"
                value={`${data.total_candidates} of ${data.total_candidates_pre_filter}`}
                explain="The right number is every put option for this symbol. The left is the subset that passed our quality filter (|Δ| ≤ 0.50, sane price, real volume)."
              />
              <Stat
                label={`${PRESET_LABEL[preset]} Eligible`}
                value={String(data.preset_eligible_counts?.[preset])}
                explain={`Of the analyzed pool, this many qualify under the ${PRESET_LABEL[preset]} preset's |Δ| cap.`}
              />
            </div>

            {/* Today's Read — 5-answer subscriber-facing summary (regime + macro + skew) */}
            <TodaysReadCard
              regime={data.regime_debug}
              macroPenalty={Number(data.macro?.combined_penalty || 0)}
              skewPenalty={Number(data.skew?.skew_penalty || 0)}
            />

            {/* HIDDEN RISK panel — V2's SkewCard, shows VIX + Skew + Trading Signal banner */}
            <SkewCard skew={data.skew} />

            {/* Macro factors panel — global freshest macro (see workstream B fix 2026-06-23).
                Fallback to data.macro if the global fetch hasn't completed yet. */}
            <NotchesMacroPanel macro={globalMacro || data.macro} asOf={globalMacroAsOf} />

            {/* AI Summaries — collapsed by default, after macro panel, before filters */}
            {(data.claude_analysis?.summary || data.chatgpt_analysis?.summary) && (
              <div style={{ marginBottom: '0.6rem', display: 'grid', gap: '0.4rem' }}>
                {data.claude_analysis?.summary && (
                  <details style={{
                    background: c.card,
                    border: `1px solid ${c.border}`,
                    borderRadius: 8,
                    padding: '0.85rem 1rem',
                  }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: c.text, fontSize: '0.85rem' }}>
                      Claude analysis
                    </summary>
                    <p style={{ marginTop: '0.75rem', whiteSpace: 'pre-wrap', fontSize: '0.82rem', color: c.muted, lineHeight: 1.5 }}>
                      {data.claude_analysis.summary}
                    </p>
                  </details>
                )}
                {data.chatgpt_analysis?.summary && (
                  <details style={{
                    background: c.card,
                    border: `1px solid ${c.border}`,
                    borderRadius: 8,
                    padding: '0.85rem 1rem',
                  }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: c.text, fontSize: '0.85rem' }}>
                      ChatGPT analysis
                    </summary>
                    <p style={{ marginTop: '0.75rem', whiteSpace: 'pre-wrap', fontSize: '0.82rem', color: c.muted, lineHeight: 1.5 }}>
                      {data.chatgpt_analysis.summary}
                    </p>
                  </details>
                )}
              </div>
            )}

            {/* Filter knobs — preset chips + sliders + source toggles */}
            <div style={{
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '0.6rem 1rem',
              marginBottom: '0.6rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.6rem',
            }}>
              {/* Row 1: scoring preset radio buttons (single choice, saved to profile) */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
                  <span
                    title="Selects the scoring weights used to rank picks. Conservative weights cushion + theta-efficiency higher; Aggressive weights credit/day + theta higher; Balanced is in between."
                    style={{ fontSize: '0.78rem', color: c.text, fontWeight: 600, cursor: 'help' }}
                  >
                    Your risk preference
                  </span>
                  <span style={{ fontSize: '0.66rem', color: c.dim }}>(saved to your profile — drives SMS alerts too)</span>
                </div>
                <div role="radiogroup" aria-label="Notches risk preset" style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {(['conservative', 'balanced', 'aggressive'] as const).map((p) => {
                    const active = preset === p;
                    return (
                      <label
                        key={p}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem', color: active ? c.text : c.muted, fontWeight: active ? 700 : 500 }}
                      >
                        <input
                          type="radio"
                          name="notches-preset"
                          checked={active}
                          onChange={() => setPreset(p)}
                          style={{ accentColor: c.purple, cursor: 'pointer' }}
                        />
                        {PRESET_LABEL[p]}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Row 2: DTE slider + source toggles. Max-|Δ| slider removed —
                  delta bands now ARE the risk filter. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(280px, 2fr) auto',
                gap: '1.25rem',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', marginBottom: '0.3rem' }}>
                    <span style={{ color: c.muted }}>Show DTEs up to</span>
                    <span style={{ fontWeight: 700 }}>{maxDte} days</span>
                  </div>
                  <input
                    type="range" min={1} max={180} step={1}
                    value={maxDte}
                    onChange={(e) => setMaxDte(Number(e.target.value))}
                    style={{ width: '100%', accentColor: c.purple }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {([
                    { key: 'WFS',     active: showWfs,    set: setShowWfs,    color: c.amber,  bg: c.amberBg },
                    { key: 'Claude',  active: showClaude, set: setShowClaude, color: c.blue,   bg: c.blueBg },
                    { key: 'ChatGPT', active: showGpt,    set: setShowGpt,    color: '#10b981', bg: '#10b98120' },
                  ] as const).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => s.set(!s.active)}
                      style={{
                        padding: '0.35rem 0.75rem', fontSize: '0.72rem', fontWeight: 700,
                        borderRadius: 4,
                        border: `1px solid ${s.active ? s.color : c.border}`,
                        background: s.active ? s.bg : c.cardAlt,
                        color: s.active ? s.color : c.dim,
                        cursor: 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}
                    >
                      {s.active ? '✓' : '○'} {s.key}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* "How to read Convergence Score" disclosure — collapsible, mirrors V2 */}
            <details style={{
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '0.6rem 0.85rem',
              marginBottom: '0.6rem',
              fontSize: '0.78rem',
              color: c.muted,
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: c.text }}>
                ▶ How to read Convergence Score
              </summary>
              <div style={{ marginTop: '0.6rem', lineHeight: 1.55 }}>
                <p style={{ margin: '0 0 0.5rem' }}>
                  Three independent systems evaluate every DTE: our quantitative <strong>WFS</strong> model, <strong>Claude</strong>, and <strong>ChatGPT</strong>. The AIs never see each other's output or the model's score.
                </p>
                <ul style={{ margin: '0 0 0.5rem 1.2rem', padding: 0 }}>
                  <li><span style={{ ...badge(c.green, c.greenBg), fontSize: '0.66rem', marginRight: 6 }}>STRONG</span> All three pick the same strike. The strongest signal we produce — when systems that evaluate risk differently all converge, the selection is more likely robust.</li>
                  <li style={{ marginTop: '0.35rem' }}><span style={{ ...badge(c.amber, c.amberBg), fontSize: '0.66rem', marginRight: 6 }}>MEDIUM</span> Two of three agree. Hover the badge to see who: "WFS + one AI" means quant and one qualitative align; "Both AIs agree" means two independent AI systems converged.</li>
                  <li style={{ marginTop: '0.35rem' }}><span style={{ ...badge(c.muted, c.cardAlt), fontSize: '0.66rem', marginRight: 6 }}>MIXED</span> Each analysis picks a different strike. The market is ambiguous at this DTE. If you trade, consider sizing smaller or selecting based on the individual trade metrics.</li>
                  <li style={{ marginTop: '0.35rem' }}><span style={{ ...badge(c.muted, c.cardAlt), fontSize: '0.66rem', marginRight: 6 }}>SOLO</span> Only one system picked at this DTE — there's nothing to compare against, so convergence isn't measurable. Treat the pick on its own merits rather than as a multi-source signal.</li>
                </ul>
                <p style={{ margin: 0, fontStyle: 'italic' }}>
                  Convergence measures agreement under uncertainty — not profit prediction. It tells you how confident the system is in its selection, not how much the trade will make.
                </p>
              </div>
            </details>

            {/* Strategy filter chips (Task 8.2) — client-side filter of the
                already-fetched signals above; does not re-fetch on click.
                Disabled/greyed chips have zero signals in the current view
                (see chipCounts / STRATEGY_CHIPS comment above). */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
              marginBottom: '0.6rem',
            }}>
              <span style={{ fontSize: '0.78rem', color: c.muted }}>Strategy:</span>
              {STRATEGY_CHIPS.map((chip) => {
                const active = strategyFilter === chip.value;
                const count = chipCounts[chip.value] ?? 0;
                const disabled = chip.value !== 'all' && count === 0;
                return (
                  <button
                    key={chip.value}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    onClick={() => setStrategyFilter(chip.value)}
                    title={disabled ? 'No signals for this strategy in the current view' : undefined}
                    style={{
                      padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none',
                      fontSize: '0.74rem', fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      background: active ? c.purple : c.cardAlt,
                      color: disabled ? c.dim : (active ? '#fff' : c.muted),
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >{chip.label}</button>
                );
              })}
            </div>

            {/* Flat sortable table — one row per DTE bucket */}
            {sortedRows.length === 0 ? (
              <div style={{
                background: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: '1.5rem',
                textAlign: 'center',
                color: c.muted,
                marginBottom: '1rem',
              }}>
                No picks pass the current filters (≤ {maxDte} DTE). Widen the DTE slider or toggle on more sources.
              </div>
            ) : (
              <>
              <RiskBriefingsHeader
                symbol={symbol}
                count={briefingsApi.briefings.length}
                snapshotTime={briefingsApi.actualSnapshotTime}
                generatedAt={briefingsApi.generatedAt}
                refreshing={briefingsApi.refreshing}
                err={briefingsApi.err}
                onRefresh={briefingsApi.refresh}
              />
              <div style={{
                background: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                overflowX: 'auto',
                overflowY: 'hidden',
                marginBottom: '0.75rem',
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.78rem',
                  tableLayout: 'auto',
                }}>
                  <thead>
                    <tr style={{
                      background: c.cardAlt,
                      color: c.muted,
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {([
                        { key: 'rank' as SortKey,    label: 'Rank',    title: 'WFS composite ranking under your current preset. 1 = highest score of the visible pool. Shown as position/total (e.g. "3/9" = third-best of nine ranked rows). Higher rank ≠ guaranteed better trade — validation shows only a weak edge — but rank 1 is the ranker\'s single top pick.' },
                        { key: 'dte' as SortKey,     label: 'DTE',     title: 'Days to expiration — calendar days from today (ET) until the option contract settles.' },
                        { key: 'strike' as SortKey,  label: 'Strike',  title: 'Put strike price — the price at which you commit to buying the underlying if assigned.' },
                        { key: 'delta' as SortKey,   label: 'Δ',       title: 'Option delta — approximate probability the option finishes in-the-money. Lower |Δ| = farther out-of-the-money = safer for sellers.' },
                        { key: 'credit' as SortKey,  label: 'Credit',  title: 'Premium collected when selling this put — paid to you upfront. Maximum profit if the put expires worthless.' },
                        { key: 'win' as SortKey,     label: 'Win %',   title: 'Probability of profit — odds the put expires worthless (you keep the full credit). Derived from delta and days to expiration.' },
                        { key: 'cushion' as SortKey, label: 'Cush', title: 'Cushion — safety buffer in points. How far the underlying can drop from spot before the strike is breached. Bigger cushion = more room to be wrong.' },
                        { key: 'rr' as SortKey,      label: 'R/R',     title: 'Reward-to-risk ratio (annualized Sharpe) — risk-adjusted return per unit of volatility. Higher = better risk-adjusted reward. Note: this is a Sharpe-style ratio, not a literal max-profit / max-loss.' },
                        { key: 'iv' as SortKey,      label: 'IV',      title: 'Implied volatility — the market\'s expectation of annualized price movement, in percent. Higher IV = richer premium, but also higher uncertainty.' },
                        { key: 'score' as SortKey,   label: 'Score',   title: 'WFS preset-weighted composite score combining credit-per-day, cushion, theta efficiency, vega exposure, and liquidity. Higher = better under your selected preset (Conservative / Balanced / Aggressive).' },
                        { key: 'target' as SortKey,  label: 'Target',  title: 'Take-profit buy-to-close price — close at this price to lock in the preset\'s profit target (Conservative 50%, Balanced 60%, Aggressive 70%).' },
                        { key: 'stop' as SortKey,    label: 'Stop',    title: 'Stop-loss buy-to-close price — buy back at this price if the trade moves against you. Multiplier of original credit (Conservative 1.5×, Balanced 2.0×, Aggressive 2.5×).' },
                      ] as const).map((h) => {
                        const isSorted = sortKey === h.key;
                        return (
                          <th
                            key={h.key}
                            onClick={() => toggleSort(h.key)}
                            title={h.title}
                            style={{
                              padding: '0.5rem 0.45rem',
                              cursor: 'help',
                              userSelect: 'none',
                              textAlign: 'left',
                              color: isSorted ? c.text : c.muted,
                              borderBottom: `1px solid ${c.border}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span style={{ borderBottom: `1px dotted ${c.dim}` }}>{h.label}</span>
                            <span style={{ opacity: 0.5, fontSize: '0.65rem', marginLeft: 3 }}>ⓘ</span>
                            {isSorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                          </th>
                        );
                      })}
                      {/* Notches mixed-table plan Task 2.4: STRAT column — strategy badge for
                          the primary candidate in this DTE row. Naked_put shows PUT (green),
                          bear_call_spread shows BCS (red), jade_lizard shows JADE (amber),
                          iron_condor shows IC (blue). Column is not sortable — it's a category
                          label, not a scalar. Ordered before Exp so the eye picks up the
                          strategy tag before the expiration date. */}
                      <th
                        title="Strategy — which options strategy generated this row's pick (PUT = naked put, BCS = bear call spread, JADE = jade lizard, IC = iron condor). Under the 'All' chip, whichever strategy scored highest at this DTE wins the row."
                        style={{ padding: '0.5rem 0.45rem', textAlign: 'center', borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap', cursor: 'help' }}
                      >
                        <span style={{ borderBottom: `1px dotted ${c.dim}` }}>Strat</span>
                        <span style={{ opacity: 0.5, fontSize: '0.65rem', marginLeft: 3 }}>ⓘ</span>
                      </th>
                      <th
                        title="Expiration date — the date this option contract settles. Format: Mon DD, YYYY."
                        style={{ padding: '0.5rem 0.45rem', textAlign: 'center', borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap', cursor: 'help' }}
                      >
                        <span style={{ borderBottom: `1px dotted ${c.dim}` }}>Exp</span>
                        <span style={{ opacity: 0.5, fontSize: '0.65rem', marginLeft: 3 }}>ⓘ</span>
                      </th>
                      <th
                        title="Source of this row's data — WFS (our quantitative model), Claude, or ChatGPT. The filled chip is the primary source whose numbers fill the row. Smaller outlined chips with ✓ / ~ show the other sources that also picked at this DTE (✓ = same strike, ~ = different)."
                        style={{ padding: '0.5rem 0.45rem', textAlign: 'center', borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap', cursor: 'help' }}
                      >
                        <span style={{ borderBottom: `1px dotted ${c.dim}` }}>Source</span>
                        <span style={{ opacity: 0.5, fontSize: '0.65rem', marginLeft: 3 }}>ⓘ</span>
                      </th>
                      <th
                        onClick={() => toggleSort('conv')}
                        title="Convergence Score — how strongly WFS, Claude, and ChatGPT agree on the strike at this DTE. STRONG = all 3 picked the same strike. MEDIUM = 2 of 3 agree. MIXED = all different (ambiguous market). See the 'How to read Convergence Score' panel above the table for the full explainer."
                        style={{
                          padding: '0.5rem 0.45rem', textAlign: 'center', cursor: 'help', userSelect: 'none',
                          color: sortKey === 'conv' ? c.text : c.muted,
                          borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ borderBottom: `1px dotted ${c.dim}` }}>Conv</span>
                        <span style={{ opacity: 0.5, fontSize: '0.65rem', marginLeft: 3 }}>ⓘ</span>
                        {sortKey === 'conv' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                      <th style={{ padding: '0.5rem 0.45rem', borderBottom: `1px solid ${c.border}` }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => {
                      // Pick a "primary" source for this row's column data: WFS first,
                      // then Claude, then GPT. Avoids all-dash rows when WFS has no
                      // candidate at this DTE but AI does.
                      const primary: any = row.wfs ?? row.claude.pick ?? row.gpt.pick ?? null;
                      const primarySource: PickSource =
                        row.wfs ? 'WFS' : row.claude.pick ? 'Claude' : row.gpt.pick ? 'ChatGPT' : 'WFS';
                      const exit = row.wfs
                        ? row.wfs.exit_recommendation?.per_preset?.[preset]
                        : synthExitGuidance(primary, preset);
                      const isExpanded = expandedRows.has(row.rowKey);
                      // Briefing lookup: match by primary's strike+expiration.
                      const rowBriefing = primary
                        ? briefingsApi.lookup.get(briefingKey(primary.strike, primary.expiration))
                        : undefined;

                      // Per-source colors used by the Source column chips below.
                      const sourceColors: Record<PickSource, { fg: string; bg: string }> = {
                        WFS:     { fg: c.amber,    bg: c.amberBg },
                        Claude:  { fg: c.blue,     bg: c.blueBg },
                        ChatGPT: { fg: '#10b981',  bg: '#10b98120' },
                      };

                      // Chip for one source. The PRIMARY source (whose data fills the row's
                      // columns) renders as a bold filled pill — clearly the "this row IS this
                      // source's pick" marker. Non-primary sources render small + dim, as
                      // "also picked at this DTE" alternatives, with ✓ if their strike matches
                      // the primary's, ~ if not.
                      const sourceChip = (
                        label: PickSource,
                        present: boolean,
                        match?: AiMatch,
                      ) => {
                        if (!present) return null;
                        const isPrimary = label === primarySource;
                        const col = sourceColors[label];
                        const matchSym = (!isPrimary && match) ? (match.strikeMatch ? ' ✓' : ' ~') : '';
                        const title = isPrimary
                          ? `This row's pick is from ${label}`
                          : (match?.strikeMatch
                              ? `${label} also picked the same strike (${match.pick.strike}) at this DTE`
                              : (match?.pick
                                  ? `${label} also picked at this DTE — strike ${match.pick.strike}P (different from primary)`
                                  : `${label} also has a pick at this DTE`));
                        // Compact short labels for table cells (full names in tooltip)
                        const SHORT: Record<PickSource, string> = { WFS: 'WFS', Claude: 'Cl', ChatGPT: 'GP' };
                        if (isPrimary) {
                          return (
                            <span
                              title={title}
                              style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.45rem',
                                borderRadius: 10,
                                fontSize: '0.66rem',
                                fontWeight: 700,
                                marginRight: 3,
                                cursor: 'help',
                                background: col.bg,
                                color: col.fg,
                                border: `1px solid ${col.bg}`,
                              }}
                            >{SHORT[label]}</span>
                          );
                        }
                        // Alternative — quieter
                        return (
                          <span
                            title={title}
                            style={{
                              display: 'inline-block',
                              padding: '0.08rem 0.3rem',
                              borderRadius: 8,
                              fontSize: '0.58rem',
                              fontWeight: 500,
                              marginRight: 2,
                              cursor: 'help',
                              background: 'transparent',
                              color: c.dim,
                              border: `1px solid ${c.border}`,
                            }}
                          >+{SHORT[label]}{matchSym}</span>
                        );
                      };

                      const isWfsRow = primarySource === 'WFS';
                      // Mark when the WFS row's pick is outside the user's preset
                      // (e.g. 1-DTE shown under Conservative). Row stays visible —
                      // the chip just signals that this exceeds their risk preference.
                      const presetMismatch = isWfsRow && row.wfs && Array.isArray(row.wfs.eligible_presets) &&
                        !row.wfs.eligible_presets.includes(preset);

                      const rank = rankByRow.get(row.rowKey);
                      const isTopRank = rank === 1;
                      return (
                        <React.Fragment key={row.rowKey}>
                          <tr
                            onClick={() => toggleRowExpand(row.rowKey)}
                            style={{
                              cursor: 'pointer',
                              borderBottom: `1px solid ${c.border}`,
                              background: isTopRank
                                ? (isExpanded ? `${c.amber}28` : `${c.amber}18`) // stronger tint for rank-1
                                : isWfsRow
                                ? (isExpanded ? `${c.amber}18` : `${c.amber}0c`)  // amber tint for WFS rows
                                : (isExpanded ? c.cardAlt : 'transparent'),
                            }}
                          >
                            <td style={{
                              padding: '0.45rem 0.45rem',
                              fontWeight: 600,
                              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                              color: rank == null ? c.dim : (isTopRank ? c.amber : c.text),
                              whiteSpace: 'nowrap',
                            }}>
                              {rank == null
                                ? <span title="No WFS score under this preset — AI-only row">—</span>
                                : (isTopRank
                                    ? <span title="Top-ranked WFS pick under your current preset">★ {rank}/{rankTotal}</span>
                                    : <>{rank}/{rankTotal}</>)}
                            </td>
                            <td style={{
                              padding: '0.45rem 0.45rem',
                              fontWeight: 700,
                              // Left accent: amber for WFS alerts, blue/green for AI-only rows.
                              borderLeft: `4px solid ${
                                isWfsRow ? c.amber
                                : primarySource === 'Claude' ? c.blue
                                : '#10b981'
                              }`,
                            }}>
                              {(() => {
                                const live = liveDte(primary?.expiration);
                                return live != null ? `${live}d` : `${row.dte}d`;
                              })()}
                              {presetMismatch && (
                                <span
                                  title={`Outside your ${PRESET_LABEL[preset]} preset (eligible only for: ${row.wfs?.eligible_presets?.join(', ')})`}
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: 6,
                                    padding: '0.08rem 0.4rem',
                                    borderRadius: 8,
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                    background: 'transparent',
                                    color: c.dim,
                                    border: `1px solid ${c.border}`,
                                    cursor: 'help',
                                  }}
                                >outside preset</span>
                              )}
                            </td>
                            <td style={{ padding: '0.45rem 0.45rem' }}>
                              {renderStrikeLegs((primary as any)?._strategy || data.strategy, primary)}
                              {rowBriefing && (
                                <span
                                  title={`AI risk: ${rowBriefing.riskLevel.toUpperCase()} - expand row to read the briefing`}
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: 6,
                                    padding: '0.08rem 0.35rem',
                                    borderRadius: 4,
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                    background: `${riskLevelColor(rowBriefing.riskLevel)}20`,
                                    color: riskLevelColor(rowBriefing.riskLevel),
                                    border: `1px solid ${riskLevelColor(rowBriefing.riskLevel)}`,
                                    verticalAlign: 'middle',
                                    cursor: 'help',
                                  }}
                                >AI*</span>
                              )}
                            </td>
                            <td style={{ padding: '0.45rem 0.45rem' }}>{primary ? fmt(primary.delta, 3) : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', color: c.green, fontWeight: 700 }}>{primary?.credit != null ? `$${fmt(primary.credit)}` : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', fontWeight: 700 }}>{primary?.prob_profit_pct != null ? `${fmt(primary.prob_profit_pct, 1)}%` : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem' }}>{primary?.points_from_atm != null ? fmt(primary.points_from_atm, 1) : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem' }}>{primary?.sharpe_annualized != null ? fmt(primary.sharpe_annualized, 2) : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', color: c.muted }}>{primary?.iv_pct != null ? `${fmt(primary.iv_pct, 1)}%` : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', color: row.wfs ? c.text : c.dim, fontSize: '0.78rem' }}>{row.wfs?.scores?.[preset] != null ? fmt(row.wfs.scores[preset], 3) : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', color: exit ? c.green : c.dim }}>{exit ? `$${fmt(exit.profit_target_credit, 2)}` : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', color: exit ? c.red : c.dim }}>{exit ? `$${fmt(exit.hard_stop_credit, 2)}` : '—'}</td>
                            {/* Notches mixed-table plan Task 2.4: STRAT badge cell — reads _strategy
                                from the primary candidate (mixed-pool annotation). Falls back to
                                data.strategy (snapshot-wide) when the annotation is absent (legacy
                                rows that pre-date the mixed pool). */}
                            <td style={{ padding: '0.45rem 0.45rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {(() => {
                                const s = (primary as any)?._strategy || data.strategy;
                                const badge = NOTCHES_STRATEGY_BADGE[s];
                                if (!badge) return <span style={{ color: c.dim, fontSize: '0.68rem' }}>—</span>;
                                return (
                                  <span
                                    title={s}
                                    style={{
                                      display: 'inline-block',
                                      padding: '0.1rem 0.4rem',
                                      borderRadius: 4,
                                      fontSize: '0.62rem',
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      background: badge.bg,
                                      color: badge.color,
                                      border: `1px solid ${badge.color}`,
                                    }}
                                  >{badge.label}</span>
                                );
                              })()}
                            </td>
                            <td title={primary?.expiration ? fmtExp(primary.expiration) : ''} style={{ padding: '0.45rem 0.45rem', color: c.muted, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>{primary?.expiration ? fmtExpShort(primary.expiration) : '—'}</td>
                            <td style={{ padding: '0.45rem 0.45rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {showWfs    && sourceChip('WFS',     !!row.wfs)}
                              {showClaude && sourceChip('Claude',  !!row.claude.pick, row.claude)}
                              {showGpt    && sourceChip('ChatGPT', !!row.gpt.pick,    row.gpt)}
                            </td>
                            <td style={{ padding: '0.45rem 0.45rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {(() => {
                                const w = row.wfs?.strike;
                                const cl = row.claude.pick?.strike;
                                const gp = row.gpt.pick?.strike;
                                const strikes = [w, cl, gp].filter((x): x is number => x != null);
                                if (strikes.length === 0) return <span style={{ color: c.dim, fontSize: '0.7rem' }}>—</span>;

                                const set = new Set(strikes);
                                const sourceName = row.wfs ? 'WFS' : row.claude.pick ? 'Claude' : 'ChatGPT';
                                const stripBase = `WFS: ${w ?? '—'} | Claude: ${cl ?? '—'} | GPT: ${gp ?? '—'}`;

                                let level: string;
                                let bg: string; let fg: string;
                                let tip: string;

                                if (strikes.length === 1) {
                                  level = 'SOLO';
                                  bg = c.cardAlt;
                                  fg = c.muted;
                                  tip = `Only ${sourceName} picked at this DTE — no other source to compare. Convergence is not measurable.\n\n${stripBase}`;
                                } else if (strikes.length === 3 && set.size === 1) {
                                  level = 'STRONG';
                                  bg = c.greenBg;
                                  fg = c.green;
                                  tip = `All 3 systems agree on ${w}. Strongest convergence signal.\n\n${stripBase}`;
                                } else if (set.size < strikes.length) {
                                  level = 'MEDIUM';
                                  bg = c.amberBg;
                                  fg = c.amber;
                                  tip = `${strikes.length} of ${strikes.length === 3 ? 3 : 2} sources agree on the strike.\n\n${stripBase}`;
                                } else {
                                  level = 'MIXED';
                                  bg = c.cardAlt;
                                  fg = c.muted;
                                  tip = `All ${strikes.length} sources picked different strikes — market is ambiguous at this DTE.\n\n${stripBase}`;
                                }
                                return (
                                  <span title={tip} style={{ ...badge(fg, bg), fontSize: '0.62rem', cursor: 'help' }}>{level}</span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '0.45rem 0.45rem', textAlign: 'right', color: c.muted, fontSize: '0.72rem' }}>
                              {isExpanded ? '▼' : '▶'}
                            </td>
                          </tr>

                          {/* Expanded — AI details, alternates, exit framework, Why.
                              The primary source's PickRow is skipped here because the
                              collapsed columns above already show its data; we just
                              expose its reasoning via a "Why" expand under those columns. */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={16} style={{ background: c.cardAlt, padding: '0.75rem 1rem', borderBottom: `1px solid ${c.border}` }}>
                                {/* AI risk briefing — inline if available for this row's primary pick */}
                                {rowBriefing && <InlineBriefingCard b={rowBriefing} />}
                                {/* Primary's reasoning — one collapsible "Why" without re-printing values */}
                                {primary && (primary as any).reasoning && (
                                  <details style={{ marginBottom: '0.6rem' }}>
                                    <summary style={{ cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: c.muted }}>
                                      ▶ Why {primarySource} picked this
                                    </summary>
                                    <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: c.text, lineHeight: 1.45 }}>
                                      {(primary as any).reasoning}
                                    </div>
                                  </details>
                                )}
                                {primary && !((primary as any).reasoning) && primarySource === 'WFS' && row.wfsReasoning && (
                                  <details style={{ marginBottom: '0.6rem' }}>
                                    <summary style={{ cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: c.muted }}>
                                      ▶ Why WFS picked this
                                    </summary>
                                    <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: c.text, lineHeight: 1.45 }}>
                                      {row.wfsReasoning}
                                    </div>
                                  </details>
                                )}
                                {showWfs && row.wfs && primarySource !== 'WFS' && (
                                  <PickRow
                                    preset={preset}
                                    symbol={symbol}
                                    source="WFS"
                                    trade={row.wfs}
                                    reasoning={row.wfsReasoning}
                                    expanded={expandedReasons.has(`row-${row.dte}-WFS`)}
                                    onToggle={() => toggleReason(`row-${row.dte}-WFS`)}
                                    regime={data.regime_debug}
                                    macroPenalty={Number(data.macro?.combined_penalty || 0)}
                                    skewPenalty={Number(data.skew?.skew_penalty || 0)}
                                  />
                                )}
                                {showClaude && row.claude.pick && primarySource !== 'Claude' && (
                                  <PickRow
                                    preset={preset}
                                    symbol={symbol}
                                    source="Claude"
                                    trade={row.claude.pick}
                                    reasoning={row.claude.pick.reasoning}
                                    expanded={expandedReasons.has(`row-${row.dte}-Claude`)}
                                    onToggle={() => toggleReason(`row-${row.dte}-Claude`)}
                                    regime={data.regime_debug}
                                    macroPenalty={Number(data.macro?.combined_penalty || 0)}
                                    skewPenalty={Number(data.skew?.skew_penalty || 0)}
                                  />
                                )}
                                {showGpt && row.gpt.pick && primarySource !== 'ChatGPT' && (
                                  <PickRow
                                    preset={preset}
                                    symbol={symbol}
                                    source="ChatGPT"
                                    trade={row.gpt.pick}
                                    reasoning={row.gpt.pick.reasoning}
                                    expanded={expandedReasons.has(`row-${row.dte}-ChatGPT`)}
                                    onToggle={() => toggleReason(`row-${row.dte}-ChatGPT`)}
                                    regime={data.regime_debug}
                                    macroPenalty={Number(data.macro?.combined_penalty || 0)}
                                    skewPenalty={Number(data.skew?.skew_penalty || 0)}
                                  />
                                )}
                                {showAlternates && row.wfsAlternates.length > 0 && (
                                  <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: `1px solid ${c.border}` }}>
                                    <div style={{ fontSize: '0.72rem', color: c.muted, marginBottom: '0.4rem', fontWeight: 600 }}>
                                      Alternate WFS candidates near {row.dte} DTE:
                                    </div>
                                    {row.wfsAlternates.map((cd, i) => (
                                      <PickRow
                                        key={`alt-${i}`}
                                        preset={preset}
                                        symbol={symbol}
                                        source="WFS"
                                        trade={cd}
                                        reasoning={undefined}
                                        expanded={expandedReasons.has(`row-${row.dte}-alt-${i}`)}
                                        onToggle={() => toggleReason(`row-${row.dte}-alt-${i}`)}
                                        regime={data.regime_debug}
                                    macroPenalty={Number(data.macro?.combined_penalty || 0)}
                                    skewPenalty={Number(data.skew?.skew_penalty || 0)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}

            {/* "Show alternates" toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.78rem',
              color: c.muted,
              marginBottom: '0.6rem',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAlternates}
                  onChange={(e) => setShowAlternates(e.target.checked)}
                />
                Show alternate WFS candidates when row is expanded
              </label>
            </div>

          </>
        )}
      </div>

      {/* Market Pulse modal — shared with V2 (SPY-based S&P proxy data) */}
      {token && (
        <MarketPulseModal token={token} open={pulseOpen} onClose={() => setPulseOpen(false)} />
      )}

      {/* Ask AI sidebar — shared component, Notches-specific endpoint + extras */}
      {token && data && !isEmpty(data) && (
        <ChatSidebar
          signal={data}
          token={token}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          endpoint="/notches/chat"
          extraBody={{ symbol }}
          examples={[
            'Why does the WFS pick differ from Claude at 30 DTE?',
            'Which DTE bucket has the best risk-reward right now?',
            'What does the elevated skew mean for these trades?',
          ]}
        />
      )}
      <RiskDisclaimer />
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const Stat: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
  explain?: string;
}> = ({ label, value, valueColor, explain }) => (
  <div title={explain}>
    <div style={{
      color: c.dim, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em',
      display: 'flex', alignItems: 'center', gap: '0.3rem',
    }}>
      <span>{label}</span>
      {explain && (
        <span
          title={explain}
          style={{
            fontSize: '0.6rem', width: 12, height: 12, lineHeight: '12px', textAlign: 'center',
            background: c.cardAlt, color: c.muted, borderRadius: '50%',
            border: `1px solid ${c.border}`, cursor: 'help',
          }}
        >?</span>
      )}
    </div>
    <div style={{ color: valueColor ?? c.text, fontWeight: 600, fontSize: '0.95rem', marginTop: '0.25rem' }}>{value}</div>
  </div>
);

// Plain-English regime label per macro tier — no formulas, no greek letters,
// no penalty numbers shown to subscribers. CALM/CAUTIOUS/STRESSED/EXTREME
// are mapped to subscriber-facing copy at render time.
const MACRO_TIER_LABEL: Record<string, string> = {
  calm:     'Calm',
  cautious: 'Slightly volatile',
  stressed: 'Volatile',
  extreme:  'Highly volatile',
  unknown:  'No data',
};

const MACRO_HEADER_LABEL: Record<string, string> = {
  calm:     'Calm — no extra caution needed',
  cautious: 'Slightly volatile — minor caution',
  stressed: 'Volatile — trade smaller, respect stops',
  extreme:  'Highly volatile — consider waiting',
  unknown:  'No data',
};

function combinedMacroTier(macro: any): string {
  // The header tier is the worst (most-stressed) of the 4 factors.
  // Order: extreme > stressed > cautious > calm > unknown.
  const RANK: Record<string, number> = { extreme: 4, stressed: 3, cautious: 2, calm: 1, unknown: 0 };
  const factors = ['oil', 'gold', 'bonds', 'rates'];
  let worst = 'calm';
  for (const k of factors) {
    const r = macro?.[k]?.regime ?? 'unknown';
    if ((RANK[r] ?? 0) > (RANK[worst] ?? 0)) worst = r;
  }
  return worst;
}

// Notches-specific macro factor panel — sits beneath V2's SkewCard. Shows the
// 4 macro factors (oil/gold/bonds/rates) Notches tracks that V2 doesn't.
// The `asOf` prop (2026-06-23) surfaces the snapshot time so users see this is
// "as of HH:MM" rather than an implicit "right now" — relevant after we
// switched to the global freshest-macro fetch.
const NotchesMacroPanel: React.FC<{ macro: any; asOf?: string | null }> = ({ macro, asOf }) => {
  if (!macro) return null;
  // Format asOf as HH:MM ET, e.g. "as of 11:00 ET"
  let asOfLabel: string | null = null;
  if (asOf) {
    try {
      const d = new Date(asOf);
      asOfLabel = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false }) + ' ET';
    } catch (_) { /* ignore */ }
  }
  const factors = [
    { key: 'oil',   label: 'Oil',           desc: 'Crude oil — sharp swings can signal energy or inflation worries.' },
    { key: 'gold',  label: 'Gold',          desc: 'Gold — sharp moves up often mean traders are seeking safety.' },
    { key: 'bonds', label: 'Long Bonds',    desc: 'Long-term US government bonds — big moves usually mean rate or inflation shocks.' },
    { key: 'rates', label: 'Rate Proxy',    desc: 'Medium-term US government bonds — tracks where interest rates are heading.' },
  ];
  const tier = combinedMacroTier(macro);
  const headerStyle = REGIME_STYLE[tier] ?? REGIME_STYLE.unknown;
  return (
    <div style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderRadius: 8,
      padding: '0.65rem 1rem',
      marginBottom: '0.6rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem', flexWrap: 'wrap', gap: '0.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Macro Backdrop</h3>
          {asOfLabel && (
            <span style={{ fontSize: '0.7rem', color: c.muted }}>as of {asOfLabel}</span>
          )}
        </div>
        <span
          style={{ ...badge(headerStyle.color, headerStyle.bg), fontSize: '0.72rem', fontWeight: 700 }}
        >
          {MACRO_HEADER_LABEL[tier] ?? MACRO_HEADER_LABEL.unknown}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '0.6rem',
      }}>
        {factors.map((f) => {
          const m: NotchesMacroFactor | undefined = macro[f.key];
          const r = m?.regime ?? 'unknown';
          const style = REGIME_STYLE[r] ?? REGIME_STYLE.unknown;
          return (
            <div key={f.key} title={f.desc} style={{
              padding: '0.5rem 0.65rem',
              background: c.cardAlt,
              border: `1px solid ${c.border}`,
              borderRadius: 6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{f.label}</span>
                  <span
                    title={f.desc}
                    style={{
                      fontSize: '0.6rem', width: 12, height: 12, lineHeight: '12px', textAlign: 'center',
                      background: c.card, color: c.muted, borderRadius: '50%',
                      border: `1px solid ${c.border}`, cursor: 'help',
                    }}
                  >?</span>
                </div>
              </div>
              <div style={{
                display: 'inline-block',
                padding: '0.1rem 0.5rem',
                borderRadius: 10,
                background: style.bg,
                color: style.color,
                fontWeight: 700,
                fontSize: '0.66rem',
              }}>{MACRO_TIER_LABEL[r] ?? MACRO_TIER_LABEL.unknown}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

